// components/GeolocationAttendance.js - VERSION COMPLÈTE CORRIGÉE + RECONNAISSANCE FACIALE
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as faceapi from 'face-api.js';
import { Home, MapPin, Camera, Clock } from 'lucide-react';
import { API_BASE } from '../config';
import { useTranslation } from '../i18n/LanguageContext';
import { useAuth } from '../App';
import { fetchPositionForPresenceConfirm, messageForPresenceGeoFailure } from '../utils/presenceGeolocation';
import LanguageSelector from './LanguageSelector';

const GPS_ACCURACY_THRESHOLD_METERS = 100;
const GPS_STABILIZATION_RETRY_DELAY_MS = 1000;   // 1s between readings
const GPS_FETCH_TIMEOUT_MS = 15000;
const GPS_READING_SAMPLE_COUNT = 6;               // 6 samples → ~6s collection window
const GPS_HARD_LOW_ACCURACY_METERS = 300;
const GPS_SOFT_ACCURACY_METERS = 150;
const GPS_SPREAD_STABLE_THRESHOLD_METERS = 50;    // samples spread < 50m = stable

const GeolocationAttendance = ({ onNavigate }) => {
  const { t, language } = useTranslation();
  const [searchParams] = useSearchParams();
  const presenceConfirmDeepLink = searchParams.get('presenceConfirm') === '1';
  const presenceConfirmAlertCycleId = searchParams.get('alertCycleId') || '';
  const { user } = useAuth();
  /** Hourly presence UI: employees & managers only — not company / super admin. */
  const skipEmployeePresencePrompts =
    user?.role === 'admin' || user?.role === 'super_admin';
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [isModuleConfigLoading, setIsModuleConfigLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false); // ✅ NOUVEAU: État séparé pour le chargement GPS
  const [isLoadingSettings, setIsLoadingSettings] = useState(false); // ✅ NOUVEAU: État séparé pour le chargement des paramètres
  const [companyLocations, setCompanyLocations] = useState([]);
  const [matchedLocation, setMatchedLocation] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [presenceDetail, setPresenceDetail] = useState(null);
  const [presenceConfirmLoading, setPresenceConfirmLoading] = useState(false);
  const [presenceDeepLinkStale, setPresenceDeepLinkStale] = useState(false);
  const [message, setMessage] = useState('');

  // 🚀 CORRIGÉ: State dynamique qui sera mis à jour depuis l'API
  const [companySettings, setCompanySettings] = useState({
    allowedRadius: 100,
    companyLocation: {
      latitude: null,
      longitude: null,
      address: "",
      name: ""
    },
    workingHours: {
      start: "08:00",
      end: "18:00"
    }
  });

  const [isWithinRadius, setIsWithinRadius] = useState(false);
  const [distance, setDistance] = useState(null);
  const [userNotes, setUserNotes] = useState('');
  const [gpsStatus, setGpsStatus] = useState('IDLE'); // IDLE | FETCHING_LOCATION | STABILIZING_GPS | FETCHING_COMPANY_LOCATIONS | MATCHING_LOCATION | READY | IN_ZONE | IN_ZONE_LOW_CONFIDENCE | OUT_OF_ZONE | UNCERTAIN_NEEDS_REFRESH | LOW_ACCURACY | ERROR | UNCONFIGURED
  const [gpsReadingStats, setGpsReadingStats] = useState({ chosenCount: 0, bestAccuracy: null });
  const isGpsResolving = ['IDLE', 'FETCHING_LOCATION', 'STABILIZING_GPS', 'FETCHING_COMPANY_LOCATIONS', 'MATCHING_LOCATION', 'READY'].includes(gpsStatus);
  const hasConfiguredLocation = (
    companySettings.companyLocation?.latitude !== null &&
    companySettings.companyLocation?.longitude !== null
  );
  const zoneStatus = ['IN_ZONE', 'IN_ZONE_LOW_CONFIDENCE', 'OUT_OF_ZONE', 'UNCERTAIN_NEEDS_REFRESH', 'LOW_ACCURACY', 'ERROR', 'UNCONFIGURED'].includes(gpsStatus)
    ? gpsStatus
    : isGpsResolving
    ? 'UNKNOWN'
    : !hasConfiguredLocation
    ? 'UNCONFIGURED'
    : (distance === null ? 'UNKNOWN' : (isWithinRadius ? 'IN_ZONE' : 'OUT_OF_ZONE'));
  const zoneVisual = {
    IN_ZONE: {
      border: 'rgba(16, 185, 129, 0.4)',
      color: '#34d399',
      label: `✅ ${t('attendance.position_authorized')}`
    },
    OUT_OF_ZONE: {
      border: 'rgba(245, 158, 11, 0.4)',
      color: '#fbbf24',
      label: `⚠️ ${t('attendance.outside_authorized_zone')}`
    },
    IN_ZONE_LOW_CONFIDENCE: {
      border: 'rgba(59, 130, 246, 0.45)',
      color: '#93c5fd',
      label: 'ℹ️ In zone (low GPS confidence)'
    },
    UNCERTAIN_NEEDS_REFRESH: {
      border: 'rgba(244, 114, 182, 0.45)',
      color: '#f9a8d4',
      label: '⚠️ GPS uncertain - refresh recommended'
    },
    LOW_ACCURACY: {
      border: 'rgba(59, 130, 246, 0.5)',
      color: '#93c5fd',
      label: '❌ Location not reliable (Low GPS accuracy)'
    },
    UNKNOWN: {
      border: 'rgba(156, 163, 175, 0.45)',
      color: '#d1d5db',
      label: '⏳ Detecting your location...'
    },
    UNCONFIGURED: {
      border: 'rgba(239, 68, 68, 0.45)',
      color: '#fca5a5',
      label: `❌ ${t('attendance.no_location_configured') || 'No location configured'}`
    },
    ERROR: {
      border: 'rgba(239, 68, 68, 0.45)',
      color: '#fca5a5',
      label: `❌ ${t('attendance.gps_unknown_error') || 'GPS error'}`
    },
  };
  const zoneStatusLabel = zoneStatus === 'UNKNOWN'
    ? (
      gpsStatus === 'STABILIZING_GPS'
        ? '📡 Improving GPS accuracy...'
        :
      gpsStatus === 'FETCHING_COMPANY_LOCATIONS'
        ? '🏢 Checking company location...'
        : gpsStatus === 'MATCHING_LOCATION'
        ? '🎯 Matching nearest office...'
        : '⏳ Detecting your location...'
    )
    : zoneVisual[zoneStatus].label;
  const getDynamicAccuracyThreshold = (radiusMeters) => {
    const radius = Number.parseFloat(radiusMeters);
    if (!Number.isFinite(radius) || radius <= 0) return GPS_ACCURACY_THRESHOLD_METERS;
    return Math.max(GPS_ACCURACY_THRESHOLD_METERS, Math.min(radius * 1.2, 250));
  };
  const getConfidenceStatus = ({ distanceMeters, radiusMeters, accuracyMeters }) => {
    if (!Number.isFinite(accuracyMeters)) return 'UNCERTAIN_NEEDS_REFRESH';
    if (accuracyMeters > GPS_HARD_LOW_ACCURACY_METERS) return 'LOW_ACCURACY';
    if (!Number.isFinite(distanceMeters) || !Number.isFinite(radiusMeters) || radiusMeters <= 0) return 'UNCERTAIN_NEEDS_REFRESH';
    if (distanceMeters <= radiusMeters && accuracyMeters <= radiusMeters) return 'IN_ZONE';
    if (distanceMeters <= radiusMeters && accuracyMeters <= GPS_SOFT_ACCURACY_METERS) return 'IN_ZONE_LOW_CONFIDENCE';
    if (distanceMeters > radiusMeters && accuracyMeters <= GPS_SOFT_ACCURACY_METERS) return 'OUT_OF_ZONE';
    return 'UNCERTAIN_NEEDS_REFRESH';
  };
  const gpsFlowRunRef = useRef(0);
  const attendanceStatusRef = useRef(null);
  const gpsOnlyAutoPunchRef = useRef(false);

  const hasValidCoordinate = (value) => value !== null && value !== undefined && Number.isFinite(Number(value));
  const hasValidLatLng = (lat, lng) => hasValidCoordinate(lat) && hasValidCoordinate(lng);

  // 🚀 NOUVEAU : États pour la reconnaissance faciale
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [stream, setStream] = useState(null);
  const [, setIsDetecting] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  // ✅ RÉACTIVÉ - Reconnaissance faciale activée par défaut (module full uniquement)
  const [useFaceRecognition, setUseFaceRecognition] = useState(true);
  /** full | simplified | ultra_simple | gps_only — défini par super admin pour l'entreprise */
  const [attendanceModuleType, setAttendanceModuleType] = useState(null);
  const [photoReady, setPhotoReady] = useState(false);
  const isFullAttendance = attendanceModuleType === 'full';
  const isPhotoOnlyMode = attendanceModuleType === 'simplified' || attendanceModuleType === 'ultra_simple';
  const isUltraSimple = attendanceModuleType === 'ultra_simple';
  const isGpsOnlyMode = attendanceModuleType === 'gps_only';
  const needsGps = attendanceModuleType === 'full' || attendanceModuleType === 'simplified' || attendanceModuleType === 'gps_only';

  /** Presence confirm uses the same GPS pipeline as punch — avoid tapping while samples are still collecting. */
  const gpsSettledForPresenceConfirm = useMemo(() => {
    if (!attendanceModuleType) return false;
    if (attendanceModuleType === 'ultra_simple') return true;
    if (!needsGps) return true;
    const blocking = new Set([
      'IDLE',
      'FETCHING_LOCATION',
      'STABILIZING_GPS',
      'FETCHING_COMPANY_LOCATIONS',
      'MATCHING_LOCATION'
    ]);
    return !isLoadingLocation && !blocking.has(gpsStatus);
  }, [attendanceModuleType, needsGps, isLoadingLocation, gpsStatus]);
  // ✅ NOUVEAU : États pour l'enregistrement de visage
  const [faceDescriptor, setFaceDescriptor] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [checkingRegistration, setCheckingRegistration] = useState(true);
  const [, setShowRegistrationPanel] = useState(false);
  const [manualOverride, setManualOverride] = useState(false);
  /** Ultra-simple: no GPS — do not require location; photo-only modes require capture + stream */
  const punchButtonsDisabled =
    isLoading ||
    (needsGps && !location) ||
    (isFullAttendance && useFaceRecognition && isModelLoaded && !manualOverride && (!isRegistered || !stream)) ||
    (isPhotoOnlyMode && (!photoReady || !stream));
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  /** Desktop: two-column layout — GPS / zone info in right sidebar (UI only). */
  const useGpsSidebar = !isMobile && needsGps && !isGpsOnlyMode;
  /** Ultra-simple: keep a two-column layout on desktop for consistency. */
  const useUltraSimpleSidebar = !isMobile && isUltraSimple;
  const useGpsOnlyCompactLayout = isGpsOnlyMode && !isMobile;
  /** Photo-only (ultra_simple): narrow centered column; gps-only is also intentionally compact. */
  const attendanceContentMaxWidth = isUltraSimple
    ? (
        isMobile
          ? 'min(100%, 440px)'
          : (useUltraSimpleSidebar ? '1200px' : '520px')
      )
    : isGpsOnlyMode
      ? (isMobile ? '560px' : '540px')
      : (isMobile ? '560px' : '1200px');
  // ✅ NOUVEAU : États pour la détection (sans timing)
  const [faceDetectionActive, setFaceDetectionActive] = useState(true); // Active la détection continue
  const capturedImageRef = useRef(null); // Image capturée lors de la détection
  // ✅ Verification only after backend matched=true — UI "you can clock in" only when true
  const [isFaceVerified, setIsFaceVerified] = useState(false);
  const [, setLastVerify] = useState(null);
  const autoArrivalDoneRef = useRef(false); // Auto arrival only once until departure
  const isSubmittingRef = useRef(false);    // Prevent duplicate submissions (auto + manual)
  // --- Camera / detection control refs ---
  const cameraInitRef = useRef(false);          // Prevent double auto camera init
  const detectionIntervalRef = useRef(null);    // Ref to the running detection interval for cleanup
  const consecutiveFaceHitsRef = useRef(0);     // Debounce: require 2 consecutive hits before locking face
  const gpsPeriodicTimerRef = useRef(null);     // Ref to periodic GPS refresh timer

  useEffect(() => {
    attendanceStatusRef.current = attendanceStatus;
  }, [attendanceStatus]);

  useEffect(() => {
    if (attendanceStatus !== null) {
      gpsOnlyAutoPunchRef.current = false;
    }
  }, [attendanceStatus]);

  // ✅ Reset all face states (camera start or after attendance) — no "clock in" until fresh verify
  const resetFaceStates = () => {
    setFaceDetected(false);
    setFaceDescriptor(null);
    setIsFaceVerified(false);
    setFaceDetectionActive(true);
    capturedImageRef.current = null;
    setPhotoReady(false);
    // Allow auto-attendance to fire again after face is retaken
    autoArrivalDoneRef.current = false;
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  };

  // ✅ Auto arrival: fires once per valid cycle when face + location + GPS zone are all ready (module full uniquement)
  useEffect(() => {
    const baseReady = isFullAttendance && faceDetected && location && stream && isRegistered &&
      !manualOverride && attendanceStatus !== 'present' && attendanceStatus !== 'absent' &&
      !isLoading && !autoArrivalDoneRef.current && useFaceRecognition && isModelLoaded;

    if (!baseReady) return;

    // Wait for GPS to fully resolve before attempting auto attendance
    if (isGpsResolving) {
      console.log('[AUTO ATTENDANCE CHECK] Waiting - GPS still resolving...');
      return;
    }
    if (!['IN_ZONE', 'IN_ZONE_LOW_CONFIDENCE', 'OUT_OF_ZONE'].includes(zoneStatus)) {
      console.log('[AUTO ATTENDANCE CHECK] Skipped - GPS zone not valid:', zoneStatus);
      return;
    }

    console.log('[AUTO ATTENDANCE CHECK] All conditions met, scheduling in 2s...', { zoneStatus });
    const tKey = setTimeout(() => {
      if (autoArrivalDoneRef.current || isSubmittingRef.current) {
        console.log('[AUTO ATTENDANCE SKIPPED] Already done or submitting');
        return;
      }
      console.log('[AUTO ATTENDANCE TRIGGERED] Calling handleAttendance(in)');
      autoArrivalDoneRef.current = true;
      setMessage(t('attendance.auto_arrival_in_progress') || 'Recording arrival...');
      handleAttendance('in');
    }, 2000);
    return () => clearTimeout(tKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullAttendance, faceDetected, location, stream, isRegistered, manualOverride, attendanceStatus, useFaceRecognition, isModelLoaded, isGpsResolving, zoneStatus]);

  useEffect(() => {
    if (!isGpsOnlyMode) return;
    if (attendanceStatus !== null) return;
    if (!location || isLoading || isGpsResolving || isSubmittingRef.current || gpsOnlyAutoPunchRef.current) return;
    if (!['IN_ZONE', 'IN_ZONE_LOW_CONFIDENCE', 'OUT_OF_ZONE'].includes(zoneStatus)) return;

    const tKey = setTimeout(() => {
      if (gpsOnlyAutoPunchRef.current || isSubmittingRef.current || attendanceStatusRef.current !== null) return;
      gpsOnlyAutoPunchRef.current = true;
      setMessage(t('attendance.auto_arrival_in_progress') || 'Recording arrival...');
      handleAttendance('in');
    }, 1200);

    return () => clearTimeout(tKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGpsOnlyMode, attendanceStatus, location, isLoading, isGpsResolving, zoneStatus]);

  // Arrêt caméra au changement de flux ou de module (évite caméra toujours active)
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [stream, attendanceModuleType]);

  // 🚀 NOUVELLE FONCTION : Headers d'authentification
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const getTokenIdentity = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return { employeeId: null, companyId: null };
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        employeeId: payload.employeeId || payload.userId || payload.id || null,
        companyId: payload.companyId || payload.company_id || null
      };
    } catch {
      return { employeeId: null, companyId: null };
    }
  };

  // Mise à jour de l'heure toutes les secondes
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Temporary unified GPS debug log for verification
  useEffect(() => {
    if (!location && !companySettings?.companyLocation) return;
    const payload = {
      userLat: location?.latitude ?? null,
      userLng: location?.longitude ?? null,
      accuracy: Number.isFinite(location?.accuracy) ? location.accuracy : null,
      companyLat: companySettings.companyLocation?.latitude ?? null,
      companyLng: companySettings.companyLocation?.longitude ?? null,
      allowedRadius: companySettings.allowedRadius ?? null,
      distance: distance ?? null
    };
    console.log('[GPS-DEBUG TEMP] ui_payload', payload);
  }, [location, companySettings, distance]);

  // Responsive: mobile pour top bar
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  // Fonction pour vérifier si le visage est enregistré
  const checkFaceRegistration = async () => {
    try {
      setCheckingRegistration(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setCheckingRegistration(false);
        return;
      }

      const response = await fetch(`${API_BASE}/api/employee/has-face`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setIsRegistered(data.hasFace);
        console.log('✅ Statut enregistrement visage:', data.hasFace ? 'ENREGISTRÉ' : 'NON ENREGISTRÉ');
      }
    } catch (error) {
      console.error('❌ Erreur vérification enregistrement:', error);
    } finally {
      setCheckingRegistration(false);
    }
  };

  // Load today's attendance status so we don't auto-arrival when user is already clocked in
  const loadAttendanceStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      const res = await fetch(`${API_BASE}/api/attendance/status`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.clockedIn === true && data.lastClockType === 'in') {
        setAttendanceStatus('present');
        console.log('[ATTENDANCE STATE UPDATED] Synced from backend → present');
      } else if (data.clockedIn === false && data.lastClockType === 'out') {
        setAttendanceStatus('absent');
        console.log('[ATTENDANCE STATE UPDATED] Synced from backend → absent (clocked out today)');
      }
      // If no record today, leave as null (handled by initial state)
      return data;
    } catch (e) {
      console.warn('Could not load attendance status', e);
      return null;
    }
  };

  const fetchPresenceStatus = useCallback(async () => {
    try {
      if (skipEmployeePresencePrompts) {
        setPresenceDetail(null);
        return null;
      }
      const token = localStorage.getItem('token');
      if (!token) return null;
      if (attendanceStatusRef.current !== 'present') {
        setPresenceDetail(null);
        return null;
      }
      const res = await fetch(`${API_BASE}/api/attendance/presence/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.activeShift) {
        setPresenceDetail(data);
        return data;
      }
      setPresenceDetail(null);
      return null;
    } catch (e) {
      console.warn('[presence] status', e);
      return null;
    }
  }, [skipEmployeePresencePrompts]);

  useEffect(() => {
    if (skipEmployeePresencePrompts) {
      setPresenceDetail(null);
      return;
    }
    if (attendanceStatus !== 'present') {
      setPresenceDetail(null);
      return;
    }
    fetchPresenceStatus();
    const t = setInterval(fetchPresenceStatus, 60000);
    return () => clearInterval(t);
  }, [attendanceStatus, fetchPresenceStatus, skipEmployeePresencePrompts]);

  /** Notification deep link: poll status so the orange banner appears after attendance/GPS hooks settle. */
  useEffect(() => {
    if (!presenceConfirmDeepLink) {
      setPresenceDeepLinkStale(false);
      return;
    }
    if (skipEmployeePresencePrompts || attendanceStatus !== 'present') {
      setPresenceDeepLinkStale(false);
      return;
    }
    let cancelled = false;
    setPresenceDeepLinkStale(false);
    (async () => {
      for (let i = 0; i < 9 && !cancelled; i += 1) {
        const data = await fetchPresenceStatus();
        if (cancelled) return;
        if (data?.pendingCycle) return;
        if (i < 8) await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) setPresenceDeepLinkStale(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    presenceConfirmDeepLink,
    presenceConfirmAlertCycleId,
    attendanceStatus,
    skipEmployeePresencePrompts,
    fetchPresenceStatus
  ]);

  const confirmPresenceClick = async () => {
    if (presenceConfirmLoading) return;
    if (!gpsSettledForPresenceConfirm) return;
    setPresenceConfirmLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }
      const geo = await fetchPositionForPresenceConfirm();
      if (!geo.ok) {
        alert(messageForPresenceGeoFailure(t, geo));
        return;
      }
      const res = await fetch(`${API_BASE}/api/attendance/presence/confirm`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          alertCycleId: presenceConfirmAlertCycleId || presenceDetail?.pendingCycle?.id || undefined,
          location: geo.position
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data.error || (language === 'fr' ? 'Erreur lors de la confirmation.' : 'Confirmation failed.'));
        return;
      }
      try {
        window.dispatchEvent(new CustomEvent('notification-refresh'));
      } catch (_e) {
        /* ignore */
      }
      setPresenceDetail(null);
      if (typeof onNavigate === 'function') {
        onNavigate('home');
      }
    } finally {
      setPresenceConfirmLoading(false);
    }
  };

  // Chargement initial : module entreprise (super admin) puis GPS / visage selon le mode
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsModuleConfigLoading(true);
      let mod = 'full';
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const res = await fetch(`${API_BASE}/api/attendance/settings`, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
          });
          const data = await res.json();
          const m = data.settings?.attendanceModuleType;
          if (m === 'full' || m === 'simplified' || m === 'ultra_simple' || m === 'gps_only') mod = m;
        }
      } catch (e) {
        console.warn('attendance module load', e);
      }
      if (cancelled) return;
      setAttendanceModuleType(mod);

      await loadAttendanceStatus();
      if (mod === 'full') {
        checkFaceRegistration();
        loadFaceModels();
      } else {
        setUseFaceRecognition(false);
        setCheckingRegistration(false);
      }
      if (mod !== 'ultra_simple') {
        getCurrentLocation({ isInitialLoad: true });
      } else {
        setGpsStatus('READY');
      }
      if (!cancelled) setIsModuleConfigLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // [FACE MODELS LOADED] → auto-start camera (module full uniquement)
  useEffect(() => {
    if (!isModelLoaded || attendanceModuleType !== 'full') return;
    console.log('[FACE MODELS LOADED] Models ready — triggering auto camera start');
    startCameraAuto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModelLoaded, attendanceModuleType]);

  // Mode photo sans reconnaissance : démarrer la caméra pour capture
  useEffect(() => {
    if (attendanceModuleType !== 'simplified' && attendanceModuleType !== 'ultra_simple') return;
    if (stream) return;
    const t = setTimeout(() => startCameraAuto(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendanceModuleType]);

  // [GPS AUTO REFRESH] — ultra_simple : pas de GPS
  useEffect(() => {
    if (isModuleConfigLoading) return;
    if (attendanceModuleType === 'ultra_simple') return;
    const GPS_PERIODIC_INTERVAL_MS = 4 * 60 * 1000; // 4 minutes

    const scheduleRefresh = () => {
      gpsPeriodicTimerRef.current = setTimeout(() => {
        console.log('[GPS AUTO REFRESH START] Periodic GPS refresh (4 min cycle)');
        getCurrentLocation({ isInitialLoad: false });
        scheduleRefresh(); // re-schedule next cycle
      }, GPS_PERIODIC_INTERVAL_MS);
    };

    scheduleRefresh();

    return () => {
      if (gpsPeriodicTimerRef.current) clearTimeout(gpsPeriodicTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendanceModuleType, isModuleConfigLoading]);

  // ✅ CRITIQUE : Attacher le stream à l'élément video quand il change
  useEffect(() => {
    if (stream && videoRef.current) {
      console.log('🔄 useEffect: Attachement du stream à l\'élément video...');
      const video = videoRef.current;
      
      // Vérifier que le stream a des tracks actifs
      const tracks = stream.getTracks();
      const videoTracks = stream.getVideoTracks();
      console.log('📹 Tracks disponibles:', {
        total: tracks.length,
        video: videoTracks.length,
        active: videoTracks.filter(t => t.readyState === 'live').length,
        enabled: videoTracks.filter(t => t.enabled).length
      });
      
      if (videoTracks.length === 0) {
        console.error('❌ Aucune piste vidéo dans le stream');
        return;
      }
      
      // S'assurer que les tracks sont activés
      videoTracks.forEach(track => {
        if (!track.enabled) {
          console.log('🔄 Activation de la track vidéo:', track.label);
          track.enabled = true;
        }
      });
      
      // Attacher le stream
      if (video.srcObject !== stream) {
        video.srcObject = stream;
        console.log('✅ Stream attaché dans useEffect');
      } else {
        console.log('ℹ️ Stream déjà attaché');
      }
      
      // Forcer les attributs de la vidéo
      video.setAttribute('autoplay', 'true');
      video.setAttribute('playsinline', 'true');
      video.setAttribute('muted', 'true');
      
      // Fonction pour démarrer la lecture
      const startPlayback = () => {
        console.log('▶️ Tentative de lecture de la vidéo...');
        video.play()
          .then(() => {
            console.log('✅ Vidéo en lecture - paused:', video.paused, 'readyState:', video.readyState);
            console.log('✅ Dimensions vidéo:', video.videoWidth, 'x', video.videoHeight);
            console.log('✅ srcObject:', video.srcObject ? 'présent' : 'absent');
            
            // Vérifier que la vidéo joue vraiment
            if (video.paused) {
              console.warn('⚠️ Vidéo toujours en pause, nouvelle tentative...');
              setTimeout(() => video.play(), 100);
            }
          })
          .catch(err => {
            console.error('❌ Erreur lors de la lecture:', err);
            console.error('   └─ Name:', err.name);
            console.error('   └─ Message:', err.message);
            // Réessayer après un court délai
            setTimeout(() => {
              video.play()
                .then(() => console.log('✅ Lecture réussie après retry'))
                .catch(e => console.error('❌ Erreur lecture (2e tentative):', e));
            }, 500);
          });
      };
      
      // Attendre que la vidéo soit prête puis forcer la lecture
      const onLoadedMetadata = () => {
        console.log('✅ Métadonnées chargées - Dimensions:', video.videoWidth, 'x', video.videoHeight);
        startPlayback();
      };
      
      const onCanPlay = () => {
        console.log('✅ Vidéo peut jouer - readyState:', video.readyState);
        startPlayback();
      };
      
      // Écouter les événements
      video.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
      video.addEventListener('canplay', onCanPlay, { once: true });
      
      // Si déjà chargé, démarrer immédiatement
      if (video.readyState >= 2) {
        console.log('ℹ️ Vidéo déjà prête, démarrage immédiat');
        onLoadedMetadata();
      }
      
      // Forcer la lecture immédiatement aussi
      startPlayback();
      
      // Vérification périodique que la vidéo joue
      const checkInterval = setInterval(() => {
        if (video.paused && video.readyState >= 2) {
          console.warn('⚠️ Vidéo en pause alors qu\'elle devrait jouer, relance...');
          video.play().catch(e => console.error('❌ Erreur relance:', e));
        }
      }, 1000);
      
      // Nettoyer quand le composant se démonte ou le stream change
      return () => {
        clearInterval(checkInterval);
        video.removeEventListener('loadedmetadata', onLoadedMetadata);
        video.removeEventListener('canplay', onCanPlay);
        // Ne pas arrêter les tracks ici car ils sont gérés par stopCamera
      };
    } else if (!stream && videoRef.current) {
      // Nettoyer si le stream est supprimé
      videoRef.current.srcObject = null;
      console.log('🧹 Stream retiré de l\'élément video');
    }
  }, [stream]);

  // ✅ Continuous face detection loop — race-condition-safe version
  // Key fix: videoRef.current is checked INSIDE the interval callback, not at effect start time.
  // This avoids the bug where the <video> element is conditionally rendered and not yet in the
  // DOM when the useEffect fires immediately after setStream() is called.
  useEffect(() => {
    // Clean up any previously running loop first
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }

    if (!isFullAttendance || !stream || !isModelLoaded || !faceDetectionActive || faceDetected) {
      return;
    }

    consecutiveFaceHitsRef.current = 0; // Reset debounce counter on loop restart
    console.log('[FACE DETECTION LOOP STARTED] Starting continuous detection...');

    detectionIntervalRef.current = setInterval(async () => {
      const video = videoRef.current;

      // Video not in DOM yet or not ready — skip this frame silently
      if (!video || video.readyState < 4 || video.videoWidth === 0) {
        console.log('[NO FACE FRAME] Video not ready yet, readyState:', video?.readyState ?? 'no-ref');
        return;
      }

      const result = await detectFace();

      if (result && result.success) {
        consecutiveFaceHitsRef.current += 1;
        console.log(`[FACE DETECTED] consecutive hits: ${consecutiveFaceHitsRef.current}`);

        // Require 2 consecutive successful detections to avoid noisy single-frame false positives
        if (consecutiveFaceHitsRef.current >= 2) {
          clearInterval(detectionIntervalRef.current);
          detectionIntervalRef.current = null;
          setFaceDetectionActive(false);
          setFaceDetected(true);
          setFaceDescriptor(result.descriptor);

          // Capture still image for attendance submission
          if (videoRef.current) {
            const canvas = document.createElement('canvas');
            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(videoRef.current, 0, 0);
            capturedImageRef.current = canvas.toDataURL('image/jpeg', 0.8);
            console.log('📸 Image captured after face confirmation');
          }

          if (attendanceStatus !== 'present' && attendanceStatus !== 'absent') {
            setMessage(`✅ ${t('attendance.face_detected_click_arrival_to_verify') || 'Face detected. Click Arrival to verify your identity.'}`);
          } else if (attendanceStatus === 'present') {
            setMessage(`✅ ${t('attendance.face_detected_click_departure_to_verify') || 'Face detected. Click Departure to verify your identity.'}`);
          }
        }
      } else {
        // Reset consecutive counter on miss — one good frame must follow another
        consecutiveFaceHitsRef.current = 0;
        console.log('[NO FACE FRAME] No face in this frame');
      }
    }, 500);

    return () => {
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
        detectionIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream, isModelLoaded, faceDetectionActive, faceDetected]);

  // 🚀 CHARGEMENT MODÈLES DEPUIS CDN FONCTIONNELS
  const loadFaceModels = async () => {
    // ✅ COMMENCER DIRECTEMENT PAR JSDELIVR (qui fonctionne)
    const cdnUrls = [
      'https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model', // jsdelivr (fonctionne)
      'https://unpkg.com/@vladmandic/face-api@1.7.12/model',      // unpkg fallback
      'https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/weights'   // ancienne version jsdelivr
    ];

    for (let i = 0; i < cdnUrls.length; i++) {
      try {
        console.log(`🔄 Tentative ${i + 1}/${cdnUrls.length} : Chargement depuis ${cdnUrls[i]}...`);
        
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(cdnUrls[i]),
          faceapi.nets.faceLandmark68Net.loadFromUri(cdnUrls[i]),
          faceapi.nets.faceRecognitionNet.loadFromUri(cdnUrls[i])
        ]);
        
        setIsModelLoaded(true);
        console.log(`✅ Modèles chargés avec succès depuis ${cdnUrls[i]}`);
        setMessage('✅ Reconnaissance faciale activée');
        return; // Succès, sortir de la fonction
        
      } catch (err) {
        console.warn(`⚠️ Échec chargement depuis ${cdnUrls[i]}:`, err.message);
        // Continuer avec le CDN suivant
        if (i === cdnUrls.length - 1) {
          // Tous les CDNs ont échoué
          console.error('❌ Tous les CDNs ont échoué. Impossible de charger les modèles.');
          setUseFaceRecognition(false);
          setMessage('⚠️ Reconnaissance faciale désactivée : impossible de charger les modèles. Pointage GPS disponible.');
        }
      }
    }
  };

  const fetchCompanyLocations = async () => {
    const locationsResponse = await fetch(`${API_BASE}/api/attendance/locations`, {
      headers: getAuthHeaders()
    });
    if (!locationsResponse.ok) {
      throw new Error(`locations API failed: ${locationsResponse.status}`);
    }
    const locationsData = await locationsResponse.json();
    if (!locationsData?.success) {
      throw new Error('locations API response invalid');
    }
    return (locationsData.locations || [])
      .filter(loc => loc.isActive)
      .filter(loc => hasValidLatLng(loc.latitude, loc.longitude))
      .map(loc => ({
        ...loc,
        latitude: Number(loc.latitude),
        longitude: Number(loc.longitude),
        radius: Number.parseFloat(loc.radius) || 100
      }));
  };

  const applyLocationMatch = (effectiveLocation, activeLocations) => {
    if (!effectiveLocation || !hasValidLatLng(effectiveLocation.latitude, effectiveLocation.longitude)) {
      return { selectedLocation: null, distanceMeters: null, within: false, candidatesChecked: activeLocations.length };
    }
    const withDistance = activeLocations.map(loc => {
      const rawDistance = calculateDistance(
        Number(effectiveLocation.latitude),
        Number(effectiveLocation.longitude),
        Number(loc.latitude),
        Number(loc.longitude)
      );
      return {
        ...loc,
        rawDistance,
        within: isInsideZone(rawDistance, loc.radius)
      };
    });
    const inZoneSorted = withDistance.filter(loc => loc.within).sort((a, b) => a.rawDistance - b.rawDistance);
    const nearestAny = withDistance.slice().sort((a, b) => a.rawDistance - b.rawDistance)[0] || null;
    const selectedLocation = inZoneSorted.length > 0 ? inZoneSorted[0] : nearestAny;
    return {
      selectedLocation,
      distanceMeters: selectedLocation ? selectedLocation.rawDistance : null,
      within: selectedLocation ? selectedLocation.within : false,
      candidatesChecked: withDistance.length
    };
  };

  // Kept for non-GPS refresh usages (e.g. post clock-in/out), without forcing a new geolocation request.
  const loadCompanySettings = async (locationOverride = null, options = {}) => {
    const showMessages = options.showMessages !== false;
    setIsLoadingSettings(true);
    try {
      if (showMessages) setMessage('🏢 Checking company location...');
      const activeLocations = await fetchCompanyLocations();
      setCompanyLocations(activeLocations);
      if (activeLocations.length === 0) {
        setMatchedLocation(null);
        setDistance(null);
        setIsWithinRadius(false);
        setCompanySettings(prev => ({
          ...prev,
          companyLocation: { latitude: null, longitude: null, address: '', name: '' }
        }));
        setGpsStatus('UNCONFIGURED');
        return;
      }

      const effectiveLocation = locationOverride || location;
      const match = applyLocationMatch(effectiveLocation, activeLocations);
      const fallbackLocation = activeLocations.find(loc => loc.isMainOffice) || activeLocations[0];
      const selectedLocation = match.selectedLocation || fallbackLocation;
      setMatchedLocation(selectedLocation || null);
      setCompanySettings(prev => ({
        ...prev,
        allowedRadius: selectedLocation?.radius || 100,
        companyLocation: {
          latitude: selectedLocation?.latitude ?? null,
          longitude: selectedLocation?.longitude ?? null,
          address: selectedLocation?.address || '',
          name: selectedLocation?.name || ''
        }
      }));

      if (match.distanceMeters !== null) {
        setDistance(Math.round(match.distanceMeters));
        setIsWithinRadius(match.within);
      } else {
        setDistance(null);
        setIsWithinRadius(false);
      }
    } catch (error) {
      console.error('❌ Erreur chargement paramètres entreprise:', error);
      if (showMessages) setMessage('❌ ' + t('attendance.error_loading_settings'));
      setGpsStatus('ERROR');
    } finally {
      setIsLoadingSettings(false);
      if (showMessages) setTimeout(() => setMessage(''), 2500);
    }
  };

  // Calcul de la distance entre deux points GPS
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Rayon de la Terre en mètres
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance en mètres
  };

  // Keep frontend zone validation aligned with backend.
  const isInsideZone = (rawDistanceMeters, radiusMeters) => {
    return rawDistanceMeters <= radiusMeters;
  };

  const getCurrentLocation = ({ isInitialLoad = false } = {}) => {
    if (isLoadingLocation) return;

    if (!navigator.geolocation) {
      setGpsStatus('ERROR');
      setLocationError("La géolocalisation n'est pas supportée par ce navigateur");
      setMessage(`❌ ${t('attendance.gps_not_supported_message')}`);
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    const runId = Date.now();
    gpsFlowRunRef.current = runId;
    // Manual GPS refresh: allow auto-attendance to re-evaluate on new location
    if (!isInitialLoad) {
      autoArrivalDoneRef.current = false;
      gpsOnlyAutoPunchRef.current = false;
      console.log('[GPS] Manual location refresh — auto-attendance flag reset');
    }
    setIsLoadingLocation(true);
    setLocationError(null);
    setGpsStatus('FETCHING_LOCATION');
    setMessage('⏳ Detecting your location...');

    const options = {
      enableHighAccuracy: true,
      timeout: GPS_FETCH_TIMEOUT_MS,
      maximumAge: 0
    };

    const requestPositionOnce = () => new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        position => resolve(position),
        error => reject(error),
        options
      );
    });

    (async () => {
      try {
        const collected = [];
        setGpsStatus('STABILIZING_GPS');
        setMessage('📡 Collecting GPS samples...');
        for (let attempt = 0; attempt < GPS_READING_SAMPLE_COUNT; attempt += 1) {
          if (gpsFlowRunRef.current !== runId) return;
          try {
            const pos = await requestPositionOnce();
            const reading = {
              latitude: Number(pos.coords.latitude),
              longitude: Number(pos.coords.longitude),
              accuracy: Number(pos.coords.accuracy),
              timestamp: new Date()
            };
            if (hasValidLatLng(reading.latitude, reading.longitude)) {
              collected.push(reading);
              setMessage(`📡 Collecting GPS samples (${collected.length}/${GPS_READING_SAMPLE_COUNT})...`);
            }
            if (attempt < GPS_READING_SAMPLE_COUNT - 1) {
              await new Promise(resolve => setTimeout(resolve, GPS_STABILIZATION_RETRY_DELAY_MS));
            }
          } catch (innerErr) {
            if (attempt === GPS_READING_SAMPLE_COUNT - 1) throw innerErr;
          }
        }

        if (collected.length === 0 || gpsFlowRunRef.current !== runId) throw new Error('NO_USABLE_GPS_READING');

        // --- Compute MEDIAN lat / lng / accuracy from all samples first ---
        const medianOf = (arr) => {
          const sorted = [...arr].sort((a, b) => a - b);
          const mid = Math.floor(sorted.length / 2);
          return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
        };
        const roughMedianLat = medianOf(collected.map(r => r.latitude));
        const roughMedianLng = medianOf(collected.map(r => r.longitude));

        // --- Outlier rejection: discard samples > 150m from rough median ---
        const GPS_OUTLIER_THRESHOLD_METERS = 150;
        const filteredSamples = collected.filter(r =>
          calculateDistance(r.latitude, r.longitude, roughMedianLat, roughMedianLng) <= GPS_OUTLIER_THRESHOLD_METERS
        );
        // Fall back to all samples if too many were filtered
        const cleanSamples = filteredSamples.length >= 3 ? filteredSamples : collected;

        console.log('[GPS SAMPLES RAW]', collected.map(r => ({
          lat: r.latitude.toFixed(6), lng: r.longitude.toFixed(6), acc: Math.round(r.accuracy)
        })));
        console.log('[GPS SAMPLES FILTERED]', cleanSamples.map(r => ({
          lat: r.latitude.toFixed(6), lng: r.longitude.toFixed(6), acc: Math.round(r.accuracy)
        })), `(${cleanSamples.length}/${collected.length} kept)`);

        // --- Final median from clean samples ---
        const medianLat = medianOf(cleanSamples.map(r => r.latitude));
        const medianLng = medianOf(cleanSamples.map(r => r.longitude));
        const validAccuracies = cleanSamples.map(r => r.accuracy).filter(a => Number.isFinite(a));
        const medianAccuracy = validAccuracies.length > 0 ? medianOf(validAccuracies) : null;

        // --- Spread on clean samples only (no outlier inflation) ---
        let spread = 0;
        if (cleanSamples.length > 1) {
          for (let i = 0; i < cleanSamples.length; i++) {
            for (let j = i + 1; j < cleanSamples.length; j++) {
              const d = calculateDistance(
                cleanSamples[i].latitude, cleanSamples[i].longitude,
                cleanSamples[j].latitude, cleanSamples[j].longitude
              );
              if (d > spread) spread = d;
            }
          }
        }

        // Raw spread (all samples) for debug only
        let rawSpread = 0;
        if (collected.length > 1) {
          for (let i = 0; i < collected.length; i++) {
            for (let j = i + 1; j < collected.length; j++) {
              const d = calculateDistance(
                collected[i].latitude, collected[i].longitude,
                collected[j].latitude, collected[j].longitude
              );
              if (d > rawSpread) rawSpread = d;
            }
          }
        }
        console.log(`[GPS SPREAD] raw=${Math.round(rawSpread)}m filtered=${Math.round(spread)}m stable_threshold=${GPS_SPREAD_STABLE_THRESHOLD_METERS}m`);

        // Stable = filtered spread is small AND we had enough clean samples
        const isStable = spread < GPS_SPREAD_STABLE_THRESHOLD_METERS && cleanSamples.length >= 3;

        const bestAccuracy = validAccuracies.length > 0 ? Math.min(...validAccuracies) : null;
        setGpsReadingStats({
          chosenCount: cleanSamples.length,
          bestAccuracy: Number.isFinite(bestAccuracy) ? Math.round(bestAccuracy) : null
        });

        const newLocation = {
          latitude: medianLat,
          longitude: medianLng,
          accuracy: medianAccuracy,
          timestamp: new Date(),
          isStable,
          sampleCount: cleanSamples.length,
          spread: Math.round(spread)
        };
        console.log('[GPS FINAL MEDIAN]', {
          lat: medianLat.toFixed(6), lng: medianLng.toFixed(6),
          acc: medianAccuracy !== null ? Math.round(medianAccuracy) : null,
          spread: Math.round(spread), isStable
        });
        setLocation(newLocation);
        console.log('[GPS-DEBUG TEMP][FE] user_position', {
          userLat: newLocation.latitude,
          userLng: newLocation.longitude,
          browserAccuracyMeters: Number.isFinite(newLocation.accuracy) ? Math.round(newLocation.accuracy) : null
        });

        setGpsStatus('FETCHING_COMPANY_LOCATIONS');
        setMessage('🏢 Checking company location...');

        // Always match against all current active authorized locations (same for check-in and clock-out)
        const activeLocations = await fetchCompanyLocations();
        if (gpsFlowRunRef.current !== runId) return;
        setCompanyLocations(activeLocations);
        const identity = getTokenIdentity();
        console.log('[GPS-DEBUG TEMP][FE] context', {
          employeeId: identity.employeeId,
          companyId: identity.companyId,
          punchContext: attendanceStatusRef.current === 'present' ? 'clock-out' : 'clock-in',
          fetchedCandidateLocations: activeLocations.map(loc => ({ id: loc.id, name: loc.name, latitude: loc.latitude, longitude: loc.longitude, radius: loc.radius }))
        });

        if (activeLocations.length === 0) {
          setMatchedLocation(null);
          setDistance(null);
          setIsWithinRadius(false);
          setCompanySettings(prev => ({
            ...prev,
            companyLocation: { latitude: null, longitude: null, address: '', name: '' }
          }));
          setGpsStatus('UNCONFIGURED');
          setMessage('⚠️ Attendance location not configured');
          return;
        }

        setGpsStatus('MATCHING_LOCATION');
        setMessage('🎯 Matching nearest authorized location...');
        const match = applyLocationMatch(newLocation, activeLocations);
        const selectedLocation = match.selectedLocation;
        setMatchedLocation(selectedLocation);
        setCompanySettings(prev => ({
          ...prev,
          allowedRadius: selectedLocation?.radius || 100,
          companyLocation: {
            latitude: selectedLocation?.latitude ?? null,
            longitude: selectedLocation?.longitude ?? null,
            address: selectedLocation?.address || '',
            name: selectedLocation?.name || ''
          }
        }));
        setDistance(match.distanceMeters !== null ? Math.round(match.distanceMeters) : null);
        setIsWithinRadius(Boolean(match.within));
        const confidenceStatus = getConfidenceStatus({
          distanceMeters: match.distanceMeters,
          radiusMeters: selectedLocation?.radius,
          accuracyMeters: newLocation.accuracy
        });
        const finalStatus = confidenceStatus;
        setGpsStatus(finalStatus);
        if (finalStatus === 'LOW_ACCURACY') setMessage('⚠️ Low GPS accuracy');
        else if (finalStatus === 'UNCERTAIN_NEEDS_REFRESH') setMessage('⚠️ Near office, but GPS accuracy is weak. Please refresh.');
        else if (finalStatus === 'IN_ZONE_LOW_CONFIDENCE') setMessage('ℹ️ In zone with low GPS confidence');
        else setMessage(isInitialLoad ? '✅ Ready' : '✅ Position updated');

        console.log('[GPS-DEBUG TEMP][FE] match_result', {
          mode: 'live_authorized_locations',
          candidateLocations: activeLocations.map(loc => ({ id: loc.id, name: loc.name, latitude: loc.latitude, longitude: loc.longitude, radius: loc.radius })),
          selectedLocationId: selectedLocation?.id || null,
          selectedLocationName: selectedLocation?.name || null,
          selectedRadius: selectedLocation?.radius ?? null,
          dynamicAccuracyThreshold: Math.round(getDynamicAccuracyThreshold(selectedLocation?.radius)),
          readingsCollected: collected.length,
          bestReadingUsed: newLocation,
          calculatedDistanceMeters: match.distanceMeters !== null ? Math.round(match.distanceMeters) : null,
          finalStatus
        });
      } catch (error) {
        let errorMessage = '';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = t('attendance.gps_permission_denied');
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = t('attendance.gps_unavailable');
            break;
          case error.TIMEOUT:
            errorMessage = t('attendance.gps_timeout');
            break;
          default:
            errorMessage = t('attendance.gps_unknown_error');
            break;
        }
        console.error('❌ Erreur géolocalisation:', errorMessage, error);
        setGpsStatus('ERROR');
        setLocationError(errorMessage);
        setMessage('❌ ' + t('attendance.update_error'));
      } finally {
        if (gpsFlowRunRef.current === runId) {
          setIsLoadingLocation(false);
          if (!isInitialLoad) setTimeout(() => setMessage(''), 3000);
        }
      }
    })();
  };

  // 🚀 NOUVEAU : Démarrer la webcam
  const startCamera = async () => {
    try {
      console.log('🎥 Démarrage de la webcam...');
      // When user is present, they are starting camera for departure — show departure flow message
      if (attendanceStatus === 'present') {
        setMessage(t('attendance.starting_camera_for_departure') || 'Starting camera for departure verification...');
      }
      // Vérifier que getUserMedia est disponible
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        const errorMsg = 'Votre navigateur ne supporte pas l\'accès à la caméra. Veuillez utiliser Chrome, Firefox ou Edge.';
        console.error('❌', errorMsg);
        alert(errorMsg);
        return;
      }
      
      // Demander l'accès à la caméra
      const videoStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          width: { ideal: 640 }, 
          height: { ideal: 480 },
          facingMode: 'user' // Caméra frontale
        }
      });
      
      console.log('✅ Stream obtenu, tracks:', videoStream.getTracks().map(t => ({ 
        kind: t.kind, 
        readyState: t.readyState,
        enabled: t.enabled,
        label: t.label
      })));
      
      // Vérifier que le stream a des tracks vidéo actifs
      const videoTracks = videoStream.getVideoTracks();
      if (videoTracks.length === 0) {
        throw new Error('Aucune piste vidéo disponible dans le stream');
      }
      
      console.log('✅ Piste vidéo active:', videoTracks[0].label);
      
      // ✅ METTRE À JOUR LE STATE AVANT TOUT + reset face (no reuse of old descriptor)
      setStream(videoStream);
      resetFaceStates();
      console.log('✅ State stream mis à jour');
      // When user is present, they started camera for departure — set departure-ready message
      if (attendanceStatus === 'present') {
        setMessage(t('attendance.camera_ready_for_departure') || 'Camera ready for departure. Click Departure when you leave.');
      }
      
      // Attendre un peu pour que React rende l'élément <video>
      await new Promise(resolve => setTimeout(resolve, 200));
      
      if (videoRef.current) {
        videoRef.current.srcObject = videoStream;
        console.log('✅ Stream attaché à videoRef.current');
        
        // Attendre que la vidéo soit prête
        return new Promise((resolve, reject) => {
          const video = videoRef.current;
          if (!video) {
            console.error('❌ videoRef.current est null après attente');
            reject(new Error('Élément vidéo non disponible'));
            return;
          }
          
          const onLoadedMetadata = () => {
            console.log('✅ Webcam prête - Dimensions:', video.videoWidth, 'x', video.videoHeight);
            console.log('✅ ReadyState:', video.readyState);
            console.log('✅ Video playing:', !video.paused);
            video.removeEventListener('loadedmetadata', onLoadedMetadata);
            video.removeEventListener('error', onError);
            resolve();
          };
          
          const onError = (e) => {
            console.error('❌ Erreur vidéo:', e);
            video.removeEventListener('loadedmetadata', onLoadedMetadata);
            video.removeEventListener('error', onError);
            reject(new Error('Erreur lors du chargement de la vidéo'));
          };
          
          video.addEventListener('loadedmetadata', onLoadedMetadata);
          video.addEventListener('error', onError);
          
          // Forcer la lecture
          video.play().catch(err => {
            console.warn('⚠️ Erreur play():', err);
          });
          
          // Si déjà chargé
          if (video.readyState >= 3) {
            onLoadedMetadata();
          }
          
          // Timeout de sécurité
          setTimeout(() => {
            if (video.readyState < 3) {
              console.error('❌ Timeout - readyState:', video.readyState);
              video.removeEventListener('loadedmetadata', onLoadedMetadata);
              video.removeEventListener('error', onError);
              reject(new Error('Timeout lors du chargement de la webcam'));
            }
          }, 5000);
        });
      } else {
        console.warn('⚠️ videoRef.current est null, mais le stream est défini dans le state');
        // Réessayer après un court délai
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.srcObject = videoStream;
            console.log('✅ Stream attaché après délai');
          }
        }, 500);
      }
    } catch (err) {
      console.error('❌ Erreur webcam:', err);
      
      const forDeparture = attendanceStatus === 'present';
      let errorMessage = 'Erreur lors du démarrage de la webcam';
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = forDeparture
          ? (t('attendance.camera_permission_for_departure') || '❌ Permission refusée\n\nPour enregistrer votre départ avec reconnaissance faciale, autorisez l\'accès à la caméra.\n\n1. Cliquez sur l\'icône de cadenas dans la barre d\'adresse\n2. Autorisez l\'accès à la caméra\n3. Rechargez la page')
          : '❌ Permission refusée\n\nVeuillez autoriser l\'accès à la caméra dans les paramètres de votre navigateur.\n\n1. Cliquez sur l\'icône de cadenas dans la barre d\'adresse\n2. Autorisez l\'accès à la caméra\n3. Rechargez la page';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage = forDeparture
          ? (t('attendance.camera_not_found_departure') || '❌ Aucune caméra trouvée. Connectez une caméra pour enregistrer votre départ avec reconnaissance faciale.')
          : '❌ Aucune caméra trouvée\n\nVeuillez connecter une caméra à votre ordinateur.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage = forDeparture
          ? (t('attendance.camera_in_use_departure') || '❌ Caméra déjà utilisée. Fermez l\'autre application pour enregistrer votre départ.')
          : '❌ Caméra déjà utilisée\n\nUne autre application utilise votre caméra. Veuillez la fermer et réessayer.';
      } else if (err.message) {
        errorMessage = `❌ ${err.message}`;
      }
      
      setStream(null); // S'assurer que le state est nettoyé
      alert(errorMessage);
    }
  };

  // 🚀 NOUVEAU : Arrêter la webcam
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
      setFaceDetected(false); // Réinitialiser l'état de détection
    }
    setPhotoReady(false);
    capturedImageRef.current = null;
    // S'assurer que videoRef est aussi nettoyé
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      console.log('✅ Webcam arrêtée et nettoyée');
    }
  };

  const capturePhotoSimple = () => {
    if (!videoRef.current || videoRef.current.readyState < 2) {
      alert(t('attendance.camera_not_ready') || 'Camera not ready');
      return;
    }
    try {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      capturedImageRef.current = canvas.toDataURL('image/jpeg', 0.85);
      setPhotoReady(true);
      if (attendanceStatus === 'present') {
        setMessage(t('attendance.photo_captured_clock_out'));
      } else {
        setMessage(t('attendance.photo_captured_clock_in'));
      }
    } catch (e) {
      console.error(e);
      alert(t('attendance.photo_capture_failed') || 'Could not capture photo');
    }
  };

  // [CAMERA INIT START] — silent auto-start with retry, no alert() spam
  const startCameraAuto = async () => {
    if (cameraInitRef.current) return; // already initialising
    if (stream) return;                // already running

    cameraInitRef.current = true;
    console.log('[CAMERA INIT START] Auto-initializing camera...');

    if (!navigator.mediaDevices?.getUserMedia) {
      console.warn('[CAMERA INIT] getUserMedia not available in this browser');
      cameraInitRef.current = false;
      return;
    }

    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const videoStream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' }
        });

        const videoTracks = videoStream.getVideoTracks();
        if (videoTracks.length === 0) throw new Error('No video tracks in stream');

        // Ensure all tracks are enabled
        videoTracks.forEach(t => { t.enabled = true; });

        setStream(videoStream);
        resetFaceStates();
        console.log('[CAMERA INIT SUCCESS] Camera started automatically on attempt', attempt);
        cameraInitRef.current = false;
        return;
      } catch (err) {
        const isPermissionError = err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError';
        if (isPermissionError) {
          // No retry for permission denial — user must grant in browser settings
          console.warn('[CAMERA INIT] Permission denied — user must allow camera access');
          cameraInitRef.current = false;
          return;
        }
        console.warn(`[CAMERA INIT] Attempt ${attempt}/${maxAttempts} failed:`, err.message);
        if (attempt < maxAttempts) {
          await new Promise(r => setTimeout(r, 1500 * attempt));
        }
      }
    }

    console.warn('[CAMERA INIT] All auto-init attempts failed — manual start required');
    cameraInitRef.current = false;
  };

  // 🚀 Détecter un visage et extraire le descripteur
  const detectFace = async () => {
    if (!videoRef.current || !canvasRef.current || !isModelLoaded || !stream) {
      console.error('❌ Webcam, canvas ou modèles non disponibles');
      return { success: false };
    }
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Vérifier que la vidéo est prête
    if (video.readyState !== 4 || video.videoWidth === 0 || video.videoHeight === 0) {
      return { success: false };
    }

    setIsDetecting(true);
    try {
      // Align canvas with displayed video size so overlay matches visible frame
      const displayWidth = video.clientWidth || video.videoWidth || 640;
      const displayHeight = video.clientHeight || video.videoHeight || 480;
      canvas.width = displayWidth;
      canvas.height = displayHeight;

      // ✅ Détection avec descripteur facial
      // scoreThreshold: 0.5 (par défaut) - Réduit à 0.3 pour faciliter la détection
      // inputSize: 320 (par défaut) - Peut être 128, 160, 224, 320, 416, 512, 608
      const detections = await faceapi
        .detectAllFaces(video, new faceapi.TinyFaceDetectorOptions({ 
          scoreThreshold: 0.5,  // Strict: reduce false detections (was 0.3)
          inputSize: 320
        }))
        .withFaceLandmarks()
        .withFaceDescriptors(); // ✅ AJOUTER pour obtenir le descripteur

      if (detections.length === 0) {
        setIsDetecting(false);
        setFaceDetected(false);
        setFaceDescriptor(null);
        
        // Effacer le canvas
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        return { success: false };
      }

      if (detections.length > 1) {
        console.warn('⚠️ Plusieurs visages détectés:', detections.length);
        setIsDetecting(false);
        return { success: false };
      }

      // ✅ Extraire le descripteur (128 valeurs)
      const descriptor = Array.from(detections[0].descriptor);
      setFaceDescriptor(descriptor);
      setFaceDetected(true);
      
      console.log('✅ Visage détecté avec descripteur (', descriptor.length, 'valeurs)');

      // Afficher le rectangle et les landmarks - en les centrant dans la boîte (verticalement et horizontalement)
      const displaySize = { width: canvas.width, height: canvas.height };
      faceapi.matchDimensions(canvas, displaySize);
      const resizedDetections = faceapi.resizeResults(detections, displaySize);
      
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Centrer le face icon (box + landmarks) au centre de la boîte
      const box = resizedDetections[0].detection?.box;
      if (box) {
        const faceCenterX = box.x + box.width / 2;
        const faceCenterY = box.y + box.height / 2;
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        ctx.save();
        ctx.translate(centerX - faceCenterX, centerY - faceCenterY);
        faceapi.draw.drawDetections(canvas, resizedDetections);
        faceapi.draw.drawFaceLandmarks(canvas, resizedDetections);
        ctx.restore();
      } else {
        faceapi.draw.drawDetections(canvas, resizedDetections);
        faceapi.draw.drawFaceLandmarks(canvas, resizedDetections);
      }

      setIsDetecting(false);
      
      // ✅ Retourner le résultat pour que l'useEffect puisse capturer l'image
      return { success: true, descriptor };
    } catch (error) {
      console.error('❌ Erreur détection:', error);
      setIsDetecting(false);
      setFaceDetected(false);
      setFaceDescriptor(null);
      return { success: false };
    }
  };

  // ✅ Fonction pour enregistrer le visage
  const registerFace = async () => {
    if (!faceDescriptor) {
      alert(`❌ ${t('attendance.face_not_detected_alert')}`);
      return;
    }

    if (faceDescriptor.length !== 128) {
      alert(`❌ ${t('attendance.face_invalid_descriptor')}`);
      return;
    }

    try {
      setMessage(t('attendance.face_registering_in_progress'));
      setIsLoading(true);
      
      const response = await fetch(`${API_BASE}/api/employee/register-face`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ faceDescriptor })
      });

      const data = await response.json();

      if (data.success) {
        setMessage(`✅ ${t('attendance.face_registered_message')}`);
        // Reset all stale face state before marking as registered
        resetFaceStates();
        setIsRegistered(true);
        setShowRegistrationPanel(false);
        // Re-fetch from server to confirm backend saved correctly
        await checkFaceRegistration();
        alert(`✅ ${t('attendance.face_registered_success')}`);
      } else {
        setMessage(`❌ ${data.error || t('attendance.face_register_error')}`);
        alert(`❌ ${data.error || t('attendance.face_register_error')}`);
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
      setMessage(`❌ ${t('attendance.face_network_error')}`);
      alert(`❌ ${t('attendance.face_network_register_error')}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Fonction pour supprimer le visage
  const deleteFace = async () => {
    if (!window.confirm(`⚠️ ${t('attendance.face_delete_confirm')}`)) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE}/api/employee/delete-face`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (data.success) {
        // Clear all local face state — no stale descriptor can survive
        setIsRegistered(false);
        setFaceDescriptor(null);
        setFaceDetected(false);
        setIsFaceVerified(false);
        setFaceDetectionActive(true);
        capturedImageRef.current = null;
        autoArrivalDoneRef.current = false;
        if (canvasRef.current) {
          const ctx = canvasRef.current.getContext('2d');
          if (ctx) ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
        // Re-fetch registration status from server to confirm clean state
        await checkFaceRegistration();
        setMessage(`✅ ${t('attendance.face_deleted_message')}`);
        alert(`✅ ${t('attendance.face_deleted')}`);
      } else {
        alert(`❌ ${data.error || t('attendance.face_delete_error')}`);
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
      alert(`❌ ${t('attendance.face_delete_error')}`);
    } finally {
      setIsLoading(false);
    }
  };

  // 🚀 FONCTION CORRIGÉE : Enregistrer le pointage via API
// 🚀 FONCTION CORRIGÉE : Justification obligatoire + traçabilité hors zone
const handleAttendance = async (type) => {
  // Prevent concurrent submissions (auto + manual race condition)
  if (isSubmittingRef.current) {
    console.log('[MANUAL ATTENDANCE SUBMIT] Skipped - already submitting');
    return;
  }
  const isUltraSimple = attendanceModuleType === 'ultra_simple';
  const isSimplified = attendanceModuleType === 'simplified';
  const isGpsOnlyMode = attendanceModuleType === 'gps_only';
  let attendanceSaved = false;
  console.log('[MANUAL ATTENDANCE SUBMIT START]', { type, zoneStatus, manualOverride, attendanceStatus, attendanceModuleType: attendanceModuleType });

  if (isPhotoOnlyMode) {
    if (!stream || !videoRef.current || videoRef.current.readyState < 2) {
      alert(t('attendance.start_camera_for_photo') || 'Please start the camera and capture a photo before clocking.');
      return;
    }
    if (!capturedImageRef.current || !photoReady) {
      alert(t('attendance.capture_photo_first') || 'Please capture a photo (button) before clocking in or out.');
      return;
    }
  }

  if (!isUltraSimple) {
    if (!location) {
      alert(`❌ ${t('attendance.gps_required')}`);
      return;
    }
    if (isGpsResolving) {
      alert('⏳ GPS validation still in progress. Please wait a moment.');
      return;
    }
    if (gpsStatus === 'ERROR') {
      alert(`❌ ${t('attendance.gps_unknown_error')}`);
      return;
    }
    if (gpsStatus === 'UNCONFIGURED') {
      alert(`❌ ${t('attendance.no_location_configured') || 'No office location configured. Please configure GPS location first.'}`);
      return;
    }
    if (gpsStatus === 'LOW_ACCURACY') {
      setMessage(`⚠️ Low GPS accuracy (±${Math.round(location?.accuracy || 0)}m). Please refresh location.`);
      setTimeout(() => setMessage(''), 3500);
      return;
    }
    if (gpsStatus === 'UNCERTAIN_NEEDS_REFRESH') {
      setMessage('⚠️ GPS uncertain. Please refresh location for a reliable result.');
      setTimeout(() => setMessage(''), 3500);
      return;
    }
    if (
      !companySettings.companyLocation ||
      companySettings.companyLocation.latitude === null ||
      companySettings.companyLocation.longitude === null
    ) {
      alert(`❌ ${t('attendance.no_location_configured') || 'No office location configured. Please configure GPS location first.'}`);
      return;
    }
    if (distance === null) {
      alert(`❌ ${t('attendance.error_loading_settings') || 'Unable to calculate distance. Please refresh position and try again.'}`);
      return;
    }
  }

  // ✅ VÉRIFICATION RECONNAISSANCE FACIALE OBLIGATOIRE
  let faceResult = null;
  let detectionTime = null;
  let faceConfidence = null;
  const detectionStartTime = Date.now(); // ✅ CORRIGÉ: Début du timing dès le début

  // VÉRIFICATION FACIALE (SAUF SI MODE MANUEL) — fresh 3-frame verify, no reuse of old descriptor (module full)
  if (isFullAttendance && useFaceRecognition && isModelLoaded && !manualOverride) {
    console.log('🔍 Vérification faciale obligatoire (fresh scan)...');
    
    if (!isRegistered) {
      alert(`❌ ${t('attendance.register_face_first')}`);
      setShowRegistrationPanel(true);
      return;
    }
    
    if (!stream) {
      alert(`❌ ${t('attendance.start_webcam_first')}`);
      return;
    }
    
    const video = videoRef.current;
    if (!video || video.readyState !== 4 || video.videoWidth === 0 || video.videoHeight === 0) {
      alert(`❌ ${t('attendance.camera_not_ready')}`);
      return;
    }
    if (video.srcObject !== stream) video.srcObject = stream;
    
    // Force fresh scan: ignore any previously locked face
    setFaceDetected(false);
    setIsFaceVerified(false);
    setFaceDescriptor(null);
    
    setIsDetecting(true);
    setMessage('🔍 Scan du visage (3 images)...');
    
    try {
      const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
      const descriptors = [];
      // Collect all 3 frames — tolerate 1 bad frame (require ≥2 successful detections)
      for (let i = 0; i < 3; i++) {
        const result = await detectFace();
        if (result && result.success && result.descriptor) {
          descriptors.push(result.descriptor);
        } else {
          console.warn(`[NO FACE FRAME] Frame ${i + 1}/3 failed during manual scan`);
        }
        if (i < 2) await delay(250);
      }

      if (descriptors.length < 2) {
        // Fewer than 2 valid frames — genuinely no face detected
        setIsDetecting(false);
        setMessage('');
        alert(`❌ ${t('attendance.face_not_detected_detection_time')}`);
        return;
      }
      
      detectionTime = Date.now() - detectionStartTime;
      console.log(`⏱️ ${descriptors.length}/3 frames captured in ${detectionTime}ms`);
      
      // Average descriptor across all successful frames
      const avgDescriptor = descriptors[0].map((_, idx) =>
        descriptors.reduce((sum, d) => sum + d[idx], 0) / descriptors.length
      );
      
      setMessage('🔍 Vérification identité...');
      const verifyResponse = await fetch(`${API_BASE}/api/employee/verify-face`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ descriptors })
      });

      const verifyData = await verifyResponse.json();
      detectionTime = Date.now() - detectionStartTime;
      setIsDetecting(false);

      if (verifyData.needsRegistration) {
        alert(`❌ ${t('attendance.register_face_first')}`);
        setShowRegistrationPanel(true);
        return;
      }

      if (!verifyData.matched || verifyData.reason === 'FACE_MISMATCH') {
        setIsFaceVerified(false);
        const msg = (t('attendance.face_only_registered_user') || t('attendance.face_mismatch_use_own') || 'Only the registered user can mark attendance. This face does not match.');
        alert(`❌ ${msg}`);
        setMessage('');
        return;
      }

      setIsFaceVerified(true);
      setLastVerify({ distance: verifyData.distance, threshold: verifyData.threshold });
      setFaceDescriptor(avgDescriptor);
      if (type === 'out') {
        setMessage(`✅ ${t('attendance.face_verified_recording_departure') || 'Face verified. Recording departure...'}`);
      } else {
        setMessage(`✅ ${t('attendance.face_recognized')}`);
      }
      
      if (videoRef.current) {
        const canvas = document.createElement('canvas');
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(videoRef.current, 0, 0);
        capturedImageRef.current = canvas.toDataURL('image/jpeg', 0.8);
      }
      faceResult = { success: true, descriptor: avgDescriptor };
      faceConfidence = verifyData.distance != null ? 1 - verifyData.distance : null;
      
    } catch (error) {
      detectionTime = Date.now() - detectionStartTime;
      console.error('❌ Erreur vérification faciale:', error);
      setIsDetecting(false);
      setIsFaceVerified(false);
      alert(`❌ Erreur lors de la vérification faciale. Veuillez réessayer.`);
      return;
    }
  } else if (isFullAttendance && manualOverride) {
    detectionTime = 0;
    console.log('⚠️ Mode manuel activé - Pas de détection faciale');
    if (!stream || !videoRef.current) {
      alert(t('attendance.camera_required_manual') || 'Camera required for manual check-in. Please start the webcam first (a photo will be taken for verification).');
      return;
    }
    // Capture photo for manual mode (used after clock-geo success)
    if (videoRef.current && videoRef.current.readyState === 4) {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(videoRef.current, 0, 0);
        capturedImageRef.current = canvas.toDataURL('image/jpeg', 0.85);
      } catch (e) {
        console.warn('Manual mode: could not capture photo', e);
      }
    }
  }

  // 🚀 NOUVEAU : Justification OBLIGATOIRE si hors zone (message selon Arrival vs Departure)
  if (!isUltraSimple && !isGpsOnlyMode && zoneStatus === 'OUT_OF_ZONE') {
    if (!userNotes.trim()) {
      const justificationKey = type === 'in' ? 'attendance.justification_required_in' : 'attendance.justification_required_out';
      const fallback = type === 'in'
        ? 'JUSTIFICATION REQUIRED!\n\nYou are outside the authorized zone.\nPlease enter a justification before clocking in.'
        : 'JUSTIFICATION REQUIRED!\n\nYou are outside the authorized zone.\nPlease enter a justification before clocking out.';
      alert(`⚠️ ${t(justificationKey) || fallback}`);
      return; // Empêcher le pointage
    }

    const confirmOutside = window.confirm(
      t('attendance.outside_zone_alert', { 
        distance: distance, 
        radius: companySettings.allowedRadius 
      }) + `\n\n✍️ ${t('common.justification') || 'Justification'} fournie: "${userNotes}"`
    );
    
    if (!confirmOutside) return;
  }

  isSubmittingRef.current = true;
  setIsLoading(true);

  try {
    const attendanceData = {
      type: type, // 'in' ou 'out'
      location: isUltraSimple ? {
        latitude: 0,
        longitude: 0,
        accuracy: null,
        timestamp: new Date().toISOString(),
        isStable: true,
        sampleCount: 1,
        spread: 0
      } : {
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
        timestamp: location.timestamp ? new Date(location.timestamp).toISOString() : null,
        // GPS stability data — backend uses these for authoritative decision
        isStable: location.isStable ?? true,
        sampleCount: location.sampleCount ?? 1,
        spread: location.spread ?? 0
      },
      // distance/zone sent for logging only; backend always recalculates
      distance: isUltraSimple ? null : distance,
      notes: userNotes,
      // ✅ AJOUTER DONNÉES RECONNAISSANCE FACIALE (backend re-verifies faceDescriptor)
      faceDetected: isFullAttendance && useFaceRecognition && isModelLoaded ? (manualOverride ? false : (faceResult ? true : false)) : null,
      faceDescriptor: (isFullAttendance && useFaceRecognition && isModelLoaded && !manualOverride && (faceResult?.descriptor || faceDescriptor)) || undefined,
      faceConfidence: faceConfidence,
      detectionTime: detectionTime,
      useFaceRecognition: isFullAttendance && useFaceRecognition && isModelLoaded,
      manualOverride: manualOverride,
      overrideReason: manualOverride ? 'Webcam ou reconnaissance faciale non fonctionnelle' : null,
      timestamp: new Date().toISOString(),
      photoBase64: (isSimplified || isUltraSimple) ? capturedImageRef.current : undefined
    };

    // 🚀 APPEL API RÉEL
    const response = await fetch(`${API_BASE}/api/attendance/clock-geo`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(attendanceData)
    });

    const data = await response.json();
    console.log('[MANUAL ATTENDANCE RESPONSE]', { ok: response.ok, success: data.success, reason: data.reason, error: data.error });

    if (response.ok && data.success) {
      attendanceSaved = true;
      // ✅ POINTAGE ENREGISTRÉ - Toujours sauvegarder une photo si possible (audit obligatoire pour tous les types)
      const attendanceId = data.data?.recordId || data.data?.id || data.attendance?.id || data.id;

      // ✅ Photo obligatoire pour audit : check-in/out, manuel, hors zone — modes photo-only : déjà envoyée au serveur avec clock-geo
      const hasVideo = videoRef.current && videoRef.current.readyState === 4 && videoRef.current.videoWidth > 0;
      if (!isPhotoOnlyMode && attendanceId && (capturedImageRef.current || hasVideo)) {
        try {
          let photoBase64 = null;
          
          if (capturedImageRef.current) {
            console.log('✅ Utilisation de l\'image capturée lors de la détection');
            photoBase64 = capturedImageRef.current;
          } else if (hasVideo) {
            console.log('📸 Capture photo depuis la vidéo (audit pour tous les pointages)');
            const video = videoRef.current;
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0);
            photoBase64 = canvas.toDataURL('image/jpeg', 0.85);
          }
          
          if (photoBase64) {
            console.log('📊 Taille photo:', Math.round(photoBase64.length / 1024), 'Ko');
            
            const descriptorToSave = (faceResult?.descriptor || (faceDescriptor && faceDescriptor.length === 128)) ? (faceResult?.descriptor || faceDescriptor) : undefined;
            const photoPayload = {
              photoBase64: photoBase64,
              attendanceId: attendanceId
            };
            if (descriptorToSave) photoPayload.faceDescriptor = descriptorToSave;

            const photoResponse = await fetch(`${API_BASE}/api/attendance/save-photo`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: JSON.stringify(photoPayload)
            });

            const photoData = await photoResponse.json();
            
            if (photoData.success) {
              console.log('✅ Photo sauvegardée pour audit:', photoData.photoPath);
            } else {
              console.warn('⚠️ Impossible de sauvegarder la photo:', photoData.error);
            }
          }
          
        } catch (photoError) {
          console.error('⚠️ Erreur sauvegarde photo:', photoError);
          console.log('ℹ️ Le pointage est enregistré mais sans photo');
        }
      } else if (attendanceId && !hasVideo && !capturedImageRef.current) {
        console.warn('⚠️ Caméra non disponible - photo non enregistrée pour cet audit');
      }

      // Message enrichi avec reconnaissance faciale et zone
      let successMessage = `✅ ${data.message}\n\n`;
      
      if (useFaceRecognition && isModelLoaded && (faceResult || isFaceVerified) && !manualOverride) {
        if (type === 'in') {
          successMessage += `✅ ${t('attendance.face_verified_with_photo') || 'Face verified – check-in recorded with photo'}\n`;
        } else {
          successMessage += `✅ ${t('attendance.departure_recorded_with_photo') || 'Face verified – departure recorded with photo'}\n`;
        }
        if (detectionTime) {
          successMessage += `   ${t('attendance.detection_time') || 'Detection time'}: ${detectionTime}ms\n`;
        }
        if (faceResult && faceResult.distance !== undefined) {
          const precision = ((1 - faceResult.distance) * 100).toFixed(1);
          successMessage += `   Match: ${precision}%\n`;
        }
        if (faceConfidence) {
          successMessage += `   ${t('attendance.confidence') || 'Confidence'}: ${Math.round(faceConfidence * 100)}%\n`;
        }
        successMessage += '\n';
      } else if (manualOverride) {
        successMessage += `⚠️ ${t('attendance.manual_mode_activated') || 'Manual mode activated'}\n`;
        successMessage += `   Photo prise pour vérification\n`;
        successMessage += `   Un administrateur vérifiera votre identité\n\n`;
      }
      
      // Use backend-authoritative zone result
      const backendZone     = data.data?.zoneStatus    || zoneStatus;
      const backendStatus   = data.data?.status        || 'OK';
      const backendDistance = data.data?.distance      ?? distance;
      const backendAccuracy = data.data?.accuracy      ?? Math.round(location?.accuracy || 0);

      if (backendZone === 'OUT_OF_ZONE') {
        successMessage += `⚠️ POINTAGE HORS ZONE [${backendStatus}]\n`;
        successMessage += `   Distance: ${backendDistance}m (${(backendDistance / 1000).toFixed(2)} km)\n`;
        successMessage += `   Limite: ${companySettings.allowedRadius}m\n`;
        successMessage += `   Justification: "${userNotes}"\n\n`;
        successMessage += `Une alerte a été créée pour votre manager.`;
        setMessage(t('attendance.outside_zone_recorded', { distance: backendDistance }));
      } else {
        successMessage += `✅ Zone: ${backendStatus} | Distance: ${backendDistance}m | GPS: ±${backendAccuracy}m\n`;
        setMessage(`✅ ${type === 'in' ? t('attendance.attendance_recorded_in') : t('attendance.attendance_recorded_out')}`);
      }

      alert(successMessage);
      
      setUserNotes('');
      setManualOverride(false); // Réinitialiser le mode manuel
      resetFaceStates();

      // Immediate local state update
      if (type === 'in') {
        setAttendanceStatus('present');
        if (isGpsOnlyMode) gpsOnlyAutoPunchRef.current = true;
        console.log('[ATTENDANCE STATE UPDATED] → present');
      } else if (type === 'out') {
        setAttendanceStatus('absent');
        if (isGpsOnlyMode) gpsOnlyAutoPunchRef.current = false;
        setMessage(t('attendance.you_have_clocked_out') || 'You have clocked out.');
        autoArrivalDoneRef.current = true; // Do not run auto arrival after departure (same session)
        console.log('[ATTENDANCE STATE UPDATED] → absent');
      }

      // Re-sync with backend to confirm the stored status (refs for departure snapshot)
      await loadAttendanceStatus();

      // Vibration si supportée
      if (navigator.vibrate) {
        navigator.vibrate([100, 50, 100]);
      }
      
      // Recharger les données
      loadCompanySettings();
    } else {
      if (data.reason === 'FACE_MISMATCH') {
        setIsFaceVerified(false);
        const msg = t('attendance.face_only_registered_user') || t('attendance.face_mismatch_use_own') || data.error || 'Only the registered user can mark attendance. This face does not match.';
        alert(`❌ ${msg}`);
        setMessage('');
      } else if (data.reason === 'OUTSIDE_RADIUS_NOT_ALLOWED') {
        const msg = data.error || t('attendance.outside_zone_not_allowed') || 'Attendance outside the authorized radius is not allowed.';
        alert(`❌ ${msg}`);
        setMessage(`❌ ${msg}`);
      } else {
        alert(`❌ ${data.error || t('attendance.attendance_error')}`);
        setMessage(`❌ ${data.error || t('attendance.attendance_error')}`);
      }
    }
  } catch (error) {
    console.error('Erreur pointage:', error);
    alert(`❌ ${t('attendance.attendance_connection_error')}`);
    setMessage(`❌ ${t('attendance.attendance_connection_error')}`);
  } finally {
    if (!attendanceSaved && isGpsOnlyMode && type === 'in' && attendanceStatusRef.current === null) {
      gpsOnlyAutoPunchRef.current = false;
    }
    isSubmittingRef.current = false;
    setIsLoading(false);
  }
};

  // Styles responsives (isMobile from state) — kept for future use
  // eslint-disable-next-line no-unused-vars
  const _styles = {
    container: {
      maxWidth: '500px',
      margin: '0 auto',
      padding: isMobile ? '15px' : '20px',
      fontFamily: 'Inter, sans-serif'
    },
    clockCard: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderRadius: '20px',
      padding: isMobile ? '20px' : '30px',
      color: 'white',
      textAlign: 'center',
      marginBottom: '20px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
    },
    time: {
      fontSize: isMobile ? '2.5rem' : '3rem',
      fontWeight: 'bold',
      margin: '10px 0',
      fontFamily: 'monospace'
    },
    date: {
      fontSize: '1rem',
      opacity: 0.9,
      marginBottom: '20px'
    },
    locationCard: {
      background: 'white',
      borderRadius: '15px',
      padding: isMobile ? '15px' : '20px',
      marginBottom: '20px',
      boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
      border: `3px solid ${isWithinRadius ? '#4CAF50' : '#FF9800'}`
    },
    statusBadge: (status) => ({
      display: 'inline-block',
      padding: '8px 16px',
      borderRadius: '20px',
      fontSize: '0.9rem',
      fontWeight: 'bold',
      background: status === 'present' ? '#4CAF50' : 
                 status === 'absent' ? '#FF5722' : '#FF9800',
      color: 'white',
      marginBottom: '15px'
    }),
    locationStatus: {
      fontSize: '1.1rem',
      fontWeight: 'bold',
      marginBottom: '10px',
      color: isWithinRadius ? '#4CAF50' : '#FF9800'
    },
    distanceText: {
      fontSize: '0.9rem',
      color: '#666',
      marginBottom: '10px'
    },
    accuracyText: {
      fontSize: '0.8rem',
      color: '#999'
    },
    buttonContainer: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: '15px',
      marginTop: '20px'
    },
    button: (type, disabled = false) => ({
      flex: 1,
      padding: isMobile ? '15px 20px' : '12px 20px',
      fontSize: isMobile ? '1.1rem' : '1rem',
      fontWeight: 'bold',
      border: 'none',
      borderRadius: '12px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      transition: 'all 0.3s ease',
      background: type === 'in' ? '#4CAF50' : '#f44336',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    }),
    textarea: {
      width: '100%',
      minHeight: '80px',
      padding: '12px',
      border: '2px solid #e0e0e0',
      borderRadius: '8px',
      fontSize: '0.9rem',
      resize: 'vertical',
      fontFamily: 'inherit',
      marginTop: '15px',
      boxSizing: 'border-box'
    },
    refreshButton: {
      background: '#2196F3',
      color: 'white',
      border: 'none',
      padding: '10px 15px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '0.9rem',
      marginTop: '10px'
    },
    warningCard: {
      background: '#FFF3CD',
      border: '2px solid #FFC107',
      borderRadius: '10px',
      padding: '15px',
      marginBottom: '20px',
      color: '#856404'
    },
    errorCard: {
      background: '#F8D7DA',
      border: '2px solid #DC3545',
      borderRadius: '10px',
      padding: '15px',
      marginBottom: '20px',
      color: '#721C24'
    },
    messageCard: {
      background: message.includes('✅') ? '#d4edda' : message.includes('⚠️') ? '#fff3cd' : '#d1ecf1',
      border: `2px solid ${message.includes('✅') ? '#c3e6cb' : message.includes('⚠️') ? '#ffeaa7' : '#bee5eb'}`,
      color: message.includes('✅') ? '#155724' : message.includes('⚠️') ? '#856404' : '#0c5460',
      borderRadius: '10px',
      padding: '15px',
      marginBottom: '20px',
      fontWeight: '500'
    },
    mapLink: {
      color: '#2196F3',
      textDecoration: 'none',
      fontSize: '0.9rem',
      marginTop: '10px',
      display: 'inline-block'
    },
    buttonPrimary: {
      background: '#007bff',
      color: 'white',
      border: 'none',
      padding: '12px 24px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '1rem',
      fontWeight: 'bold',
      marginTop: '10px',
      width: '100%',
      maxWidth: '300px'
    },
    buttonSecondary: {
      background: '#6c757d',
      color: 'white',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '0.9rem',
      marginTop: '10px',
      width: '100%',
      maxWidth: '300px'
    }
  };

  const manualOverridePanel =
    useFaceRecognition && isModelLoaded && stream ? (
      <div
        style={{
          marginTop: useGpsSidebar ? 0 : '16px',
          padding: '10px 12px',
          background: '#fff3cd',
          border: '1px solid #e0b040',
          borderRadius: '8px',
        }}
      >
        <div
          style={{
            fontWeight: 700,
            fontSize: '0.8rem',
            marginBottom: '6px',
            color: '#856404',
          }}
        >
          ⚠️ {t('attendance.manual_override_mode')}
        </div>

        <div
          style={{
            fontSize: '0.72rem',
            marginBottom: '8px',
            color: '#856404',
            lineHeight: 1.4,
          }}
        >
          {t('attendance.manual_override_description')}
        </div>

        <div
          style={{
            padding: '6px 8px',
            background: manualOverride ? '#f8d7da' : '#fff',
            border: '1px solid #e6c35c',
            borderRadius: '6px',
            marginBottom: '8px',
          }}
        >
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              cursor: 'pointer',
              margin: 0,
            }}
          >
            <input
              type="checkbox"
              checked={manualOverride}
              onChange={(e) => {
                const checked = e.target.checked;
                if (checked) {
                  if (
                    !window.confirm(
                      '⚠️ ATTENTION !\n\n' +
                        'En activant le mode manuel :\n' +
                        '- Votre pointage sera marqué comme "manuel"\n' +
                        '- Une photo sera quand même prise\n' +
                        '- Un administrateur pourra vérifier votre identité\n' +
                        '- En cas de fraude, des sanctions pourront être appliquées\n\n' +
                        'Confirmez-vous vouloir continuer ?'
                    )
                  ) {
                    return;
                  }
                }
                setManualOverride(checked);
              }}
              style={{
                width: '13px',
                height: '13px',
                minWidth: '13px',
                minHeight: '13px',
                margin: '2px 0 0 0',
                cursor: 'pointer',
                flexShrink: 0,
                accentColor: '#856404',
              }}
            />
            <span
              style={{
                color: manualOverride ? '#721c24' : '#5c4a12',
                fontSize: '0.72rem',
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              Activer le pointage manuel (sans reconnaissance faciale)
            </span>
          </label>
        </div>

        {manualOverride && (
          <div
            style={{
              padding: '8px 10px',
              background: '#f8d7da',
              border: '1px solid #dc3545',
              borderRadius: '6px',
              marginBottom: '8px',
            }}
          >
            <div
              style={{
                fontWeight: 'bold',
                fontSize: '0.75rem',
                color: '#721c24',
                marginBottom: '4px',
              }}
            >
              ⚠️ Mode manuel activé
            </div>
            <div
              style={{
                fontSize: '0.7rem',
                color: '#721c24',
                lineHeight: 1.35,
              }}
            >
              Votre pointage sera enregistré MAIS marqué comme "manuel" et soumis à vérification.
            </div>
          </div>
        )}

        {manualOverride && (
          <div
            style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={() => {
                if (window.confirm(`⚠️ ${t('attendance.manual_clock_in_confirm')}`)) {
                  handleAttendance('in');
                }
              }}
              disabled={isLoading}
              style={{
                padding: '8px 14px',
                background: '#ffc107',
                color: '#000',
                border: '1px solid #856404',
                borderRadius: '6px',
                fontSize: '0.78rem',
                fontWeight: 700,
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              ⚠️ {t('attendance.arrival')} {t('attendance.manual_override')}
            </button>

            <button
              type="button"
              onClick={() => {
                if (window.confirm(`⚠️ ${t('attendance.manual_clock_out_confirm')}`)) {
                  handleAttendance('out');
                }
              }}
              disabled={isLoading}
              style={{
                padding: '8px 14px',
                background: '#ffc107',
                color: '#000',
                border: '1px solid #856404',
                borderRadius: '6px',
                fontSize: '0.78rem',
                fontWeight: 700,
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              ⚠️ {t('attendance.departure')} {t('attendance.manual_override')}
            </button>
          </div>
        )}
      </div>
    ) : null;

  const presencePromptBanner = null;

  const presenceDeepLinkHint =
    presenceConfirmDeepLink &&
    !skipEmployeePresencePrompts &&
    attendanceStatus === 'present' &&
    !presenceDetail?.pendingCycle &&
    presenceDeepLinkStale ? (
      <div
        style={{
          background: 'rgba(148, 163, 184, 0.12)',
          border: '1px solid rgba(148, 163, 184, 0.35)',
          borderRadius: '12px',
          padding: '12px 14px',
          marginBottom: '16px',
          fontSize: '0.8rem',
          color: 'rgba(255,255,255,0.82)',
          lineHeight: 1.4
        }}
      >
        {language === 'fr'
          ? 'Aucune confirmation en attente. Vous avez peut-être déjà confirmé, ou le rappel était en retard.'
          : 'No pending presence check right now. You may have already confirmed, or this reminder was delayed.'}
      </div>
    ) : null;

  const clockBox = (
    <div
      className="attendance-clock-box"
      style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '14px',
        padding: isMobile ? '14px 16px' : '16px 20px',
        color: 'white',
        textAlign: 'center',
        marginBottom: '16px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)',
      }}
    >
      <div
        style={{
          fontSize: isMobile ? '1.65rem' : '2rem',
          fontWeight: 700,
          margin: '8px 0',
          fontFamily: 'monospace',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}
      >
        <Clock size={isMobile ? 22 : 26} />
        {currentTime.toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })}
      </div>
      <div
        style={{
          fontSize: isMobile ? '0.75rem' : '0.8rem',
          opacity: 0.85,
          marginBottom: '10px',
          fontWeight: 500,
          lineHeight: 1.35,
        }}
      >
        {currentTime.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })}
      </div>
      {attendanceStatus && (
        <div
          style={{
            display: 'inline-block',
            padding: '6px 12px',
            borderRadius: '999px',
            fontSize: '0.72rem',
            fontWeight: 700,
            background:
              attendanceStatus === 'present'
                ? 'linear-gradient(135deg, #10b981, #059669)'
                : attendanceStatus === 'absent'
                  ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                  : 'linear-gradient(135deg, #f59e0b, #d97706)',
            color: 'white',
            marginBottom: '0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          }}
        >
          {attendanceStatus === 'present'
            ? `✅ ${t('attendance.status_present')}`
            : attendanceStatus === 'absent'
              ? `❌ ${t('attendance.status_absent')}`
              : `⏳ ${t('attendance.status_pending')}`}
        </div>
      )}
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'relative',
      overflowY: 'visible',
    }}>
      {/* Header: same bar for all; on mobile title smaller, 2 lines max, no mid-word break */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: isMobile ? '12px 16px 10px' : '24px 20px 20px',
        background: 'transparent',
        backdropFilter: 'none',
        borderBottom: 'none',
        zIndex: 999,
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '12px',
          minWidth: 0
        }}>
          {onNavigate && (
            <button
              onClick={() => onNavigate('home')}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '10px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                backdropFilter: 'blur(10px)',
                flexShrink: 0,
                width: '44px',
                height: '44px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title={t('common.back_to_home')}
            >
              <Home size={20} />
            </button>
          )}
          <div style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '2px'
          }}>
            <div style={{
              fontSize: isMobile ? '0.95rem' : 'clamp(28px, 5vw, 40px)',
              fontWeight: 700,
              letterSpacing: '-0.5px',
              lineHeight: 1.25,
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '8px' : '12px',
              minWidth: 0,
              wordBreak: 'keep-all',
              overflowWrap: 'break-word',
              whiteSpace: 'normal'
            }}>
              <MapPin size={isMobile ? 22 : 32} style={{ flexShrink: 0, alignSelf: 'flex-start', marginTop: isMobile ? 2 : 0 }} />
              <span style={{ flex: 1 }}>
                {isFullAttendance
                  ? `${t('attendance.title')} — GPS + ${t('attendance.face_detection')}`
                  : attendanceModuleType === 'simplified'
                    ? `${t('attendance.title')} — ${t('attendance.module_photo_gps')}`
                    : attendanceModuleType === 'gps_only'
                      ? `${t('attendance.title')} — ${t('attendance.module_gps_only')}`
                    : attendanceModuleType === 'ultra_simple'
                      ? `${t('attendance.title')} — ${t('attendance.module_photo_only')}`
                      : t('attendance.title')}
              </span>
            </div>
            <p style={{
              fontSize: isMobile ? '0.8rem' : 'clamp(14px, 2vw, 16px)',
              color: 'rgba(255, 255, 255, 0.7)',
              margin: 0,
              letterSpacing: '0.2px',
              fontWeight: 500,
              lineHeight: 1.3
            }}>
              {attendanceModuleType === 'ultra_simple'
                ? (t('attendance.gps_disabled_policy') || 'GPS disabled for your company.')
                : attendanceModuleType === 'gps_only'
                  ? (t('attendance.gps_only_policy') || 'GPS-only attendance is active for your company.')
                : t('attendance.gps_required')}
            </p>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="attendance-content" style={{
        paddingTop: isMobile ? '88px' : (isGpsOnlyMode ? '100px' : '120px'),
        paddingBottom: '24px',
        paddingLeft: '16px',
        paddingRight: '16px',
        maxWidth: attendanceContentMaxWidth,
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box'
      }}>
      {isModuleConfigLoading ? (
        <div style={{
          marginTop: '20px',
          background: 'rgba(255, 255, 255, 0.06)',
          border: '1px solid rgba(255, 255, 255, 0.16)',
          borderRadius: '16px',
          padding: '24px',
          textAlign: 'center',
          color: 'rgba(255, 255, 255, 0.9)'
        }}>
          <div style={{ fontSize: '1.4rem', marginBottom: '8px' }}>⏳</div>
          <div style={{ fontWeight: 600 }}>
            {t('attendance.loading') || 'Loading attendance...'}
          </div>
        </div>
      ) : (
      <>
      {/* Language selector in body on mobile for responsiveness */}
      {isMobile && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '16px'
        }}>
          <LanguageSelector
            style={{
              background: 'rgba(10, 14, 39, 0.9)',
              padding: '8px 12px',
              borderRadius: 12,
              backdropFilter: 'blur(12px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              border: '1px solid rgba(255,255,255,0.1)'
            }}
          />
        </div>
      )}
      <div
        className="attendance-page-layout"
        style={
          (useGpsSidebar || useUltraSimpleSidebar)
            ? {
                display: 'grid',
                gridTemplateColumns: useGpsSidebar
                  ? 'minmax(0, 2fr) minmax(0, 1fr)'
                  : 'minmax(0, 1fr) minmax(0, 1fr)',
                gridTemplateRows: 'auto auto',
                gridTemplateAreas: '"attTop attSide" "attBot attSide"',
                gap: '16px',
                alignItems: 'start',
                width: '100%',
              }
            : {
                display: 'flex',
                flexDirection: 'column',
                gap: isGpsOnlyMode ? '10px' : '16px',
                width: '100%',
                alignItems: useGpsOnlyCompactLayout ? 'center' : 'stretch',
              }
        }
      >
      <div
        className="attendance-col-top"
        style={(useGpsSidebar || useUltraSimpleSidebar)
          ? { gridArea: 'attTop', minWidth: 0 }
          : (useGpsOnlyCompactLayout ? { width: '100%', maxWidth: '360px', minWidth: 0 } : {})}
      >
      {!(useGpsSidebar || useUltraSimpleSidebar) && clockBox}
      {!(useGpsSidebar || useUltraSimpleSidebar) && presencePromptBanner}
      {!(useGpsSidebar || useUltraSimpleSidebar) && presenceDeepLinkHint}

      {/* 🚀 NOUVEAU : Message de statut — when Present, show "You are clocked in" instead of "Click Arrival or Departure" */}
      {(() => {
        const isClickToVerify = message && (message.includes('Arrival or Departure') || message.includes('Arrivée ou Départ') || message.includes('verify your identity') || message.includes('vérifier votre identité'));
        const topContent = attendanceStatus === 'present' && (!message || isClickToVerify)
          ? (`✅ ${t('attendance.you_are_clocked_in')}` || '✅ You are clocked in. Click Departure when you leave.')
          : attendanceStatus === 'absent'
          ? (`✅ ${t('attendance.you_have_clocked_out')}` || '✅ You have clocked out.')
          : message;
        if (!topContent) return null;
        return (
        <div style={{
          background: topContent.includes('✅')
            ? 'rgba(16, 185, 129, 0.15)'
            : topContent.includes('⚠️')
            ? 'rgba(245, 158, 11, 0.15)'
            : 'rgba(59, 130, 246, 0.15)',
          color: topContent.includes('✅')
            ? '#34d399'
            : topContent.includes('⚠️')
            ? '#fbbf24'
            : '#93c5fd',
          border: `1px solid ${topContent.includes('✅') ? 'rgba(16, 185, 129, 0.4)' : topContent.includes('⚠️') ? 'rgba(245, 158, 11, 0.4)' : 'rgba(59, 130, 246, 0.4)'}`,
          borderRadius: '10px',
          padding: '10px 12px',
          marginBottom: '16px',
          fontWeight: 600,
          fontSize: '12px',
          lineHeight: 1.35,
          backdropFilter: 'blur(10px)'
        }}>
          {topContent}
        </div>
      );})()}

      {/* ✅ Message informatif - Statut reconnaissance faciale */}
      {useFaceRecognition && !isModelLoaded && (
        <div style={{
          background: 'rgba(245, 158, 11, 0.15)',
          border: '2px solid rgba(245, 158, 11, 0.4)',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '24px',
          backdropFilter: 'blur(10px)'
        }}>
          <div style={{ fontWeight: 700, marginBottom: '8px', color: '#fbbf24', fontSize: '15px' }}>
            ⏳ {t('attendance.loading_facial_models')}
          </div>
          <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)' }}>
            {t('attendance.please_wait_loading_models')}
          </div>
        </div>
      )}

      {/* Mode photo + GPS ou photo seule (sans reconnaissance faciale) */}
      {isPhotoOnlyMode && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
          padding: '16px',
          marginBottom: '16px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
        }}>
          <h3 style={{ color: '#ffffff', fontSize: '16px', fontWeight: 700, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Camera size={18} />
            {attendanceModuleType === 'ultra_simple'
              ? (t('attendance.module_photo_only') || 'Photo only')
              : (t('attendance.module_photo_gps') || 'Photo + GPS')}
          </h3>
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)', marginBottom: '12px' }}>
            {t('attendance.photo_mode_hint') || 'Capture a photo for each clock-in or out.'}
          </p>
          {!stream ? (
            <button type="button" onClick={startCamera} style={{
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              color: '#fff', border: 'none', padding: '10px 16px', borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '13px',
              boxShadow: '0 2px 10px rgba(59, 130, 246, 0.28)',
            }}>
              <Camera size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              {t('attendance.start_camera') || 'Start camera'}
            </button>
          ) : (
            <div style={{
              display: (attendanceModuleType === 'ultra_simple' && !isMobile) ? 'grid' : 'block',
              gridTemplateColumns: (attendanceModuleType === 'ultra_simple' && !isMobile) ? 'minmax(0, 1fr)' : undefined,
              gridTemplateRows: (attendanceModuleType === 'ultra_simple' && !isMobile) ? 'auto auto' : undefined,
              minWidth: 0,
            }}>
              <div style={{
                minWidth: 0,
              }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    width: '100%',
                    maxWidth: (attendanceModuleType === 'ultra_simple' && !isMobile) ? '100%' : '240px',
                    maxHeight: (attendanceModuleType === 'ultra_simple' && !isMobile) ? '360px' : '200px',
                    borderRadius: '10px',
                    background: '#000',
                    display: 'block',
                    margin: '0 auto 8px',
                    objectFit: 'cover',
                  }}
                />
              </div>

              <div style={{
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
              }}>
                <div style={{
                  display: 'flex',
                  gap: '10px',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: (attendanceModuleType === 'ultra_simple' && !isMobile) ? 'center' : undefined,
                  marginTop: (attendanceModuleType === 'ultra_simple' && !isMobile) ? '6px' : undefined,
                }}>
                  <button type="button" onClick={capturePhotoSimple} style={{
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    color: '#fff', border: 'none', padding: '10px 16px', borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '13px',
                    boxShadow: '0 2px 10px rgba(16, 185, 129, 0.25)',
                  }}>
                    {t('attendance.capture_photo') || 'Capture photo'}
                  </button>
                  <button type="button" onClick={stopCamera} style={{
                    background: 'rgba(239, 68, 68, 0.22)',
                    color: '#fca5a5', border: '1px solid rgba(239,68,68,0.45)', padding: '10px 16px', borderRadius: '10px', cursor: 'pointer',
                    fontSize: '13px', fontWeight: 700,
                  }}>
                    {t('attendance.stop_camera')}
                  </button>
                  {photoReady && (
                    <span style={{ color: '#34d399', fontWeight: 600, fontSize: '12px' }}>✓ {t('attendance.photo_ready') || 'Photo ready'}</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 🚀 Section reconnaissance faciale - MASQUÉE SI DÉSACTIVÉE */}
      {isFullAttendance && useFaceRecognition && isModelLoaded && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
          padding: '14px 16px',
          marginBottom: '20px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
        }}>
          <h3 style={{
            color: '#ffffff',
            fontSize: '12px',
            fontWeight: 700,
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            letterSpacing: '0.02em',
          }}>
            <Camera size={14} />
            {t('attendance.facial_recognition_title')}
          </h3>
          {!stream ? (
            <div>
              <button
                onClick={startCamera}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                  color: '#ffffff',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  margin: '0 auto',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 10px rgba(59, 130, 246, 0.28)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(59, 130, 246, 0.3)';
                }}
              >
                <Camera size={16} />
                {attendanceStatus === 'present'
                  ? (t('attendance.start_webcam_for_departure') || 'Start webcam for departure')
                  : (t('attendance.start_camera') || 'Démarrer la webcam')}
              </button>
              <p style={{
                fontSize: '11px',
                color: 'rgba(255, 255, 255, 0.65)',
                marginTop: '8px',
                textAlign: 'center',
                lineHeight: 1.35,
              }}>
                ⚠️ {t('attendance.camera_permission_required')}
              </p>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '14px',
              alignItems: 'stretch',
              width: '100%',
              minWidth: 0,
            }}>
              <div style={{
                flex: isMobile ? 'unset' : '1 1 0',
                width: isMobile ? '100%' : '50%',
                minWidth: 0,
              }}>
                <div style={{ position: 'relative' }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{
                      width: '100%',
                      height: 'auto',
                      minHeight: isMobile ? '220px' : '260px',
                      maxHeight: isMobile ? '320px' : '380px',
                      backgroundColor: '#000',
                      borderRadius: '10px',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                    onLoadedMetadata={() => {
                      console.log('📹 Événement loadedmetadata déclenché');
                      if (videoRef.current) {
                        videoRef.current.play().catch(e => console.error('Erreur play onLoadedMetadata:', e));
                      }
                    }}
                    onCanPlay={() => {
                      console.log('📹 Événement canplay déclenché');
                      if (videoRef.current && videoRef.current.paused) {
                        videoRef.current.play().catch(e => console.error('Erreur play onCanPlay:', e));
                      }
                    }}
                    onPlay={() => {
                      console.log('▶️ Événement play déclenché - Vidéo en lecture');
                    }}
                    onError={(e) => {
                      console.error('❌ Erreur vidéo:', e);
                      console.error('   └─ Code:', e.target?.error?.code);
                      console.error('   └─ Message:', e.target?.error?.message);
                    }}
                  />
                  <canvas
                    ref={canvasRef}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'none'
                    }}
                  />
                </div>
              </div>

              <div style={{
                flex: isMobile ? 'unset' : '1 1 0',
                width: isMobile ? '100%' : '50%',
                minWidth: 0,
              }}>
                <button
                  type="button"
                  onClick={stopCamera}
                  style={{
                    background: 'rgba(239, 68, 68, 0.22)',
                    border: '1px solid rgba(239, 68, 68, 0.45)',
                    color: '#fca5a5',
                    padding: '10px 16px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 700,
                    marginTop: '0',
                    marginBottom: '8px',
                    width: '100%',
                    boxSizing: 'border-box',
                    transition: 'all 0.3s ease',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 2px 10px rgba(239, 68, 68, 0.2)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(239, 68, 68, 0.32)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(239, 68, 68, 0.22)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  🛑 {t('attendance.stop_camera')}
                </button>
                {videoRef.current && videoRef.current.readyState === 4 && (
                  <p style={{ fontSize: '0.72rem', color: '#34d399', marginBottom: '8px', textAlign: 'center' }}>
                    ✅ {t('attendance.camera_active_ready')}
                  </p>
                )}

              {checkingRegistration ? (
                <div style={{ padding: '6px', textAlign: 'center', color: 'rgba(255,255,255,0.65)', fontSize: '0.72rem' }}>
                  ⏳ {t('attendance.checking_registration')}
                </div>
              ) : isRegistered ? (
                <div style={{ marginTop: '4px' }}>
                  <div style={{
                    padding: '6px 8px',
                    marginBottom: '6px',
                    background: 'rgba(16, 185, 129, 0.12)',
                    border: '1px solid rgba(16, 185, 129, 0.35)',
                    borderRadius: '6px',
                  }}>
                    <div style={{ color: '#6ee7b7', fontWeight: 700, fontSize: '0.75rem', lineHeight: 1.35 }}>
                      ✅ {t('attendance.face_registered')}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.68rem', lineHeight: 1.35, marginTop: '2px' }}>
                      {t('attendance.face_identity_verified')}
                    </div>
                  </div>
                  <div style={{
                    padding: '6px 8px',
                    marginBottom: '8px',
                    background: (isFaceVerified || faceDetected || attendanceStatus === 'present' || attendanceStatus === 'absent')
                      ? 'rgba(16, 185, 129, 0.1)'
                      : 'rgba(239, 68, 68, 0.1)',
                    border: `1px solid ${(isFaceVerified || faceDetected || attendanceStatus === 'present' || attendanceStatus === 'absent') ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.25)'}`,
                    borderRadius: '6px',
                  }}>
                    <div style={{
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      lineHeight: 1.35,
                      color: (isFaceVerified || faceDetected || attendanceStatus === 'present' || attendanceStatus === 'absent') ? '#a7f3d0' : '#fca5a5',
                    }}>
                      {attendanceStatus === 'present'
                        ? (faceDetected ? `✅ ${t('attendance.face_detected_click_departure_to_verify') || 'Face detected. Click Departure to verify your identity.'}` : `✅ ${t('attendance.you_are_clocked_in') || 'You are clocked in. Click Departure when you leave.'}`)
                        : attendanceStatus === 'absent'
                        ? `✅ ${t('attendance.you_have_clocked_out') || 'You have clocked out.'}`
                        : isFaceVerified
                        ? `✅ ${t('attendance.face_ready')}`
                        : faceDetected
                          ? `✅ ${t('attendance.face_detected_click_arrival_to_verify') || 'Face detected. Click Arrival to verify your identity.'}`
                          : `⏳ ${t('attendance.awaiting_detection')}`}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={deleteFace}
                    disabled={isLoading}
                    style={{
                      padding: '10px 16px',
                      width: '100%',
                      boxSizing: 'border-box',
                      background: isLoading ? 'rgba(107, 114, 128, 0.5)' : 'linear-gradient(135deg, #ef4444, #dc2626)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      fontWeight: 700,
                      boxShadow: isLoading ? 'none' : '0 2px 10px rgba(239, 68, 68, 0.28)',
                    }}
                  >
                    🗑️ {t('attendance.delete_face')}
                  </button>
                </div>
              ) : (
                <div style={{ marginTop: '4px' }}>
                  <div style={{
                    padding: '6px 8px',
                    background: 'rgba(245, 158, 11, 0.12)',
                    border: '1px solid rgba(245, 158, 11, 0.35)',
                    borderRadius: '6px',
                    marginBottom: '6px',
                  }}>
                    <div style={{ color: '#fbbf24', fontWeight: 700, marginBottom: '2px', fontSize: '0.72rem' }}>
                      ⚠️ {t('attendance.face_not_registered')}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.68rem', lineHeight: 1.35 }}>
                      {t('attendance.position_face_camera')}
                    </div>
                  </div>
                  <div style={{
                    padding: '6px 8px',
                    background: faceDetected ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                    border: `1px solid ${faceDetected ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.25)'}`,
                    borderRadius: '6px',
                    marginBottom: '8px',
                  }}>
                    <div style={{ fontWeight: 700, fontSize: '0.72rem', color: faceDetected ? '#6ee7b7' : '#fca5a5' }}>
                      {faceDetected ? `✅ ${t('attendance.face_detected')}` : `⏳ ${t('attendance.awaiting_detection')}`}
                    </div>
                    {faceDetected && (
                      <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.65rem', marginTop: '4px' }}>
                        Descripteur: {faceDescriptor ? `${faceDescriptor.length} valeurs` : 'N/A'}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={registerFace}
                    disabled={!faceDetected || isLoading}
                    style={{
                      padding: '10px 16px',
                      width: '100%',
                      boxSizing: 'border-box',
                      background: faceDetected && !isLoading ? 'linear-gradient(135deg, #10b981, #059669)' : 'rgba(107, 114, 128, 0.5)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: faceDetected && !isLoading ? 'pointer' : 'not-allowed',
                      fontSize: '13px',
                      fontWeight: 700,
                      boxShadow: faceDetected && !isLoading ? '0 2px 10px rgba(16, 185, 129, 0.28)' : 'none',
                    }}
                  >
                    {isLoading ? `⏳ ${t('attendance.face_registering')}` : (faceDetected ? `✅ ${t('attendance.register_face')}` : `⏳ ${t('attendance.detection_in_progress')}`)}
                  </button>
                </div>
              )}
            </div>
            </div>
          )}
        </div>
      )}
      </div>

      <div
        className="attendance-col-side"
        style={
          (useGpsSidebar || useUltraSimpleSidebar)
            ? {
                gridArea: 'attSide',
                minWidth: 0,
                alignSelf: 'start',
              }
            : (useGpsOnlyCompactLayout ? { width: '100%', maxWidth: '360px', minWidth: 0 } : {})
        }
      >
      {(useGpsSidebar || useUltraSimpleSidebar) && clockBox}
      {(useGpsSidebar || useUltraSimpleSidebar) && presencePromptBanner}
      {(useGpsSidebar || useUltraSimpleSidebar) && presenceDeepLinkHint}
      {locationError && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid rgba(239, 68, 68, 0.45)',
          borderRadius: '8px',
          padding: '10px 12px',
          marginBottom: '12px',
          color: '#fca5a5',
          backdropFilter: 'blur(10px)'
        }}>
          <strong style={{ fontSize: '0.8rem', fontWeight: 700, display: 'block', marginBottom: '6px' }}>
            ❌ {t('attendance.error_geolocation')}
          </strong>
          <div style={{ marginBottom: '8px', fontSize: '0.72rem', lineHeight: 1.4 }}>{locationError}</div>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              getCurrentLocation();
            }}
            disabled={isLoadingLocation}
            style={{
              background: isLoadingLocation ? 'rgba(239, 68, 68, 0.5)' : 'linear-gradient(135deg, #ef4444, #dc2626)',
              color: '#ffffff',
              border: 'none',
              padding: '10px 16px',
              borderRadius: '10px',
              cursor: isLoadingLocation ? 'wait' : 'pointer',
              fontSize: '13px',
              fontWeight: 700,
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 10px rgba(239, 68, 68, 0.28)',
              opacity: isLoadingLocation ? 0.7 : 1
            }}
            onMouseEnter={(e) => {
              if (!isLoadingLocation) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(239, 68, 68, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoadingLocation) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
              }
            }}
          >
            {isLoadingLocation ? '⏳ ' + t('attendance.refreshing') : '🔄 ' + t('attendance.try_again')}
          </button>
        </div>
      )}

      {!useFaceRecognition && !isPhotoOnlyMode && (
        <div style={{
          background: 'rgba(245, 158, 11, 0.15)',
          border: '1px solid rgba(245, 158, 11, 0.45)',
          borderRadius: '8px',
          padding: '10px 12px',
          marginBottom: '12px',
          backdropFilter: 'blur(10px)'
        }}>
          <div style={{ fontWeight: 700, marginBottom: '5px', color: '#fbbf24', fontSize: '0.8rem' }}>
            ℹ️ {t('attendance.gps_only_mode')}
          </div>
          <div style={{ fontSize: '0.72rem', lineHeight: 1.4, color: 'rgba(255, 255, 255, 0.82)' }}>
            {t('attendance.face_recognition_disabled_msg')}<br />
            {t('attendance.gps_only_message')}
          </div>
        </div>
      )}

      {/* Statut de la localisation */}
      {needsGps && (isGpsResolving || (location && !locationError)) && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${zoneVisual[zoneStatus].border}`,
          borderRadius: '10px',
          padding: '10px 12px',
          marginBottom: '12px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
        }}>
          <div style={{
            fontSize: '0.8rem',
            fontWeight: 700,
            marginBottom: '8px',
            color: zoneVisual[zoneStatus].color,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            lineHeight: 1.35,
          }}>
            {zoneStatusLabel}
          </div>

          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
            <a 
              href={location ? `https://www.google.com/maps?q=${location.latitude},${location.longitude}` : '#'}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#93c5fd',
                textDecoration: 'none',
                fontSize: '11px',
                fontWeight: 600,
                padding: '5px 9px',
                lineHeight: 1.25,
                background: 'rgba(59, 130, 246, 0.18)',
                border: '1px solid rgba(59, 130, 246, 0.35)',
                borderRadius: '8px',
                transition: 'all 0.2s',
                pointerEvents: location ? 'auto' : 'none',
                opacity: location ? 1 : 0.6,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)';
              }}
            >
              🗺️ {t('attendance.view_on_maps')}
            </a>
            
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🖱️ Clic sur Actualiser position');
                getCurrentLocation();
              }}
              disabled={isLoadingLocation}
              style={{
                background: isLoadingLocation ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.18)',
                border: '1px solid rgba(59, 130, 246, 0.35)',
                color: isLoadingLocation ? 'rgba(147, 197, 253, 0.5)' : '#93c5fd',
                padding: '5px 9px',
                borderRadius: '8px',
                cursor: isLoadingLocation ? 'wait' : 'pointer',
                fontSize: '11px',
                fontWeight: 600,
                lineHeight: 1.25,
                transition: 'all 0.2s',
                opacity: isLoadingLocation ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (!isLoadingLocation) {
                  e.currentTarget.style.background = 'rgba(59, 130, 246, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoadingLocation) {
                  e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)';
                }
              }}
            >
              {isLoadingLocation ? `⏳ ${t('attendance.gps_updating')}` : `🔄 ${t('attendance.refresh_position')}`}
            </button>
          </div>
        </div>
      )}

      {/* Avertissement si hors zone */}
      {location &&
        companySettings.companyLocation?.latitude !== null &&
        companySettings.companyLocation?.longitude !== null &&
        distance !== null &&
        zoneStatus === 'OUT_OF_ZONE' && (
        <div style={{
          background: 'rgba(245, 158, 11, 0.15)',
          border: '1px solid rgba(245, 158, 11, 0.45)',
          borderRadius: '8px',
          padding: '10px 12px',
          marginBottom: '12px',
          backdropFilter: 'blur(10px)'
        }}>
          <strong style={{ color: '#fbbf24', fontSize: '0.8rem', fontWeight: 700, display: 'block', marginBottom: '5px' }}>
            ⚠️ {t('attendance.attention')}
          </strong>
          <div style={{ color: 'rgba(255, 255, 255, 0.82)', fontSize: '0.72rem', lineHeight: 1.4 }}>
            {t('attendance.outside_zone_warning')}
          </div>
        </div>
      )}

      {/* Warning when GPS is too imprecise to trust zone result */}
      {location &&
        hasConfiguredLocation &&
        zoneStatus === 'LOW_ACCURACY' && (
        <div style={{
          background: 'rgba(59, 130, 246, 0.15)',
          border: '1px solid rgba(59, 130, 246, 0.45)',
          borderRadius: '8px',
          padding: '10px 12px',
          marginBottom: '12px',
          backdropFilter: 'blur(10px)'
        }}>
          <strong style={{ color: '#93c5fd', fontSize: '0.8rem', fontWeight: 700, display: 'block', marginBottom: '5px' }}>
            ℹ️ Low GPS accuracy
          </strong>
          <div style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.72rem', lineHeight: 1.4 }}>
            GPS precision is currently ±{Math.round(location?.accuracy || 0)}m. Signal is too weak for reliable validation. Move to open sky and refresh position.
          </div>
        </div>
      )}

      {/* In-zone but low-confidence accuracy */}
      {location &&
        hasConfiguredLocation &&
        zoneStatus === 'IN_ZONE_LOW_CONFIDENCE' && (
        <div style={{
          background: 'rgba(59, 130, 246, 0.12)',
          border: '1px solid rgba(59, 130, 246, 0.38)',
          borderRadius: '8px',
          padding: '10px 12px',
          marginBottom: '12px',
          backdropFilter: 'blur(10px)'
        }}>
          <strong style={{ color: '#93c5fd', fontSize: '0.8rem', fontWeight: 700, display: 'block', marginBottom: '5px' }}>
            ℹ️ In zone with low confidence
          </strong>
          <div style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.72rem', lineHeight: 1.4 }}>
            You appear to be in authorized zone, but GPS accuracy is moderate. Refresh if you want a stronger reading.
          </div>
        </div>
      )}

      {/* Uncertain GPS reading needs refresh */}
      {location &&
        hasConfiguredLocation &&
        zoneStatus === 'UNCERTAIN_NEEDS_REFRESH' && (
        <div style={{
          background: 'rgba(244, 114, 182, 0.12)',
          border: '1px solid rgba(244, 114, 182, 0.38)',
          borderRadius: '8px',
          padding: '10px 12px',
          marginBottom: '12px',
          backdropFilter: 'blur(10px)'
        }}>
          <strong style={{ color: '#f9a8d4', fontSize: '0.8rem', fontWeight: 700, display: 'block', marginBottom: '5px' }}>
            ⚠️ GPS uncertain
          </strong>
          <div style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.72rem', lineHeight: 1.4 }}>
            Near office detected but confidence is not strong enough. Please refresh location for stable validation.
          </div>
        </div>
      )}

      {/* Unknown zone: location is configured but distance is not resolved yet */}
      {location &&
        hasConfiguredLocation &&
        !isGpsResolving &&
        zoneStatus === 'UNKNOWN' && (
        <div style={{
          background: 'rgba(156, 163, 175, 0.15)',
          border: '1px solid rgba(156, 163, 175, 0.45)',
          borderRadius: '8px',
          padding: '10px 12px',
          marginBottom: '12px',
          backdropFilter: 'blur(10px)'
        }}>
          <strong style={{ color: '#d1d5db', fontSize: '0.8rem', fontWeight: 700, display: 'block', marginBottom: '5px' }}>
            ⏳ Zone status unknown
          </strong>
          <div style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.72rem', lineHeight: 1.4 }}>
            GPS or location selection is still resolving. Please refresh position to complete validation.
          </div>
        </div>
      )}

      {isLoading && needsGps && !location && (
        <div style={{
          textAlign: 'center',
          padding: useGpsSidebar ? '12px 10px' : '24px 16px',
          background: useGpsSidebar ? 'rgba(255, 255, 255, 0.06)' : '#f0f0f0',
          borderRadius: '8px',
          marginBottom: '12px',
          marginTop: useGpsSidebar ? 0 : '8px',
          border: useGpsSidebar ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
          color: useGpsSidebar ? 'rgba(255, 255, 255, 0.9)' : undefined,
        }}>
          <div style={{ fontSize: '1.35rem', marginBottom: '6px' }}>📍</div>
          <div style={{ fontSize: '0.8rem', fontWeight: 600 }}>{t('attendance.getting_location')}</div>
          <div style={{ fontSize: '0.72rem', color: useGpsSidebar ? 'rgba(255,255,255,0.65)' : '#666', marginTop: '4px', lineHeight: 1.35 }}>
            {t('attendance.ensure_location_access')}
          </div>
        </div>
      )}
      {useGpsSidebar && manualOverridePanel}
      </div>

      <div
        className="attendance-col-bottom"
        style={useGpsSidebar
          ? { gridArea: 'attBot', minWidth: 0 }
          : (useGpsOnlyCompactLayout ? { width: '100%', maxWidth: '360px', minWidth: 0 } : {})}
      >
      {/* Zone de notes */}
      {!isGpsOnlyMode && <div style={{
        marginBottom: '16px',
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        padding: '10px 12px'
      }}>
        <label style={{
          display: 'block',
          fontWeight: 700,
          marginBottom: '5px',
          color: '#ffffff',
          fontSize: '0.8rem',
          lineHeight: 1.35,
        }}>
          💬 {isGpsOnlyMode
            ? (t('attendance.activity_notes_label') || t('attendance.notes_label'))
            : t('attendance.notes_label')} {(!isGpsOnlyMode && zoneStatus === 'OUT_OF_ZONE') ? t('attendance.notes_required') : t('attendance.notes_optional')}
        </label>
        <textarea
          value={userNotes}
          onChange={(e) => setUserNotes(e.target.value)}
          placeholder={
            isGpsOnlyMode
            ? (t('attendance.activity_notes_placeholder') || t('attendance.notes_placeholder'))
            : zoneStatus === 'OUT_OF_ZONE' ?
            t('attendance.notes_placeholder_zone') :
            t('attendance.notes_placeholder')
          }
          style={{
            width: '100%',
            minHeight: '56px',
            padding: '6px 8px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '8px',
            fontSize: '0.72rem',
            resize: 'vertical',
            fontFamily: 'inherit',
            background: 'rgba(255, 255, 255, 0.1)',
            color: '#ffffff',
            outline: 'none',
            transition: 'all 0.2s',
            boxSizing: 'border-box',
            lineHeight: 1.4,
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
          }}
          maxLength={500}
        />
        <div style={{ textAlign: 'right', fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.5)', marginTop: '3px' }}>
          {t('attendance.characters_count', { count: userNotes.length })}
        </div>
      </div>}

      {/* Boutons de pointage */}
      <div style={{
        display: 'flex',
        flexDirection: (isGpsOnlyMode && !isMobile) ? 'row' : 'column',
        alignItems: 'center',
        gap: isGpsOnlyMode ? '8px' : '10px',
        marginBottom: isGpsOnlyMode ? '8px' : '16px'
      }}>
        <button
          onClick={() => handleAttendance('in')}
          disabled={punchButtonsDisabled}
          style={{
            width: '100%',
            maxWidth: isGpsOnlyMode ? ((isMobile ? '220px' : '160px')) : '100%',
            padding: isGpsOnlyMode ? '9px 14px' : (isMobile ? '11px 14px' : '10px 16px'),
            fontSize: isGpsOnlyMode ? '12px' : (isMobile ? '13px' : '13px'),
            fontWeight: 700,
            border: 'none',
            borderRadius: isGpsOnlyMode ? '9px' : '10px',
            cursor: punchButtonsDisabled ? 'not-allowed' : 'pointer',
            opacity: punchButtonsDisabled ? 0.6 : 1,
            transition: 'all 0.3s ease',
            background: punchButtonsDisabled
              ? 'rgba(107, 114, 128, 0.5)'
              : 'linear-gradient(135deg, #10b981, #059669)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isGpsOnlyMode ? '5px' : '6px',
            boxShadow: punchButtonsDisabled
              ? 'none'
              : '0 2px 10px rgba(16, 185, 129, 0.28)'
          }}
          onMouseEnter={(e) => {
            if (!punchButtonsDisabled) {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 14px rgba(16, 185, 129, 0.35)';
            }
          }}
          onMouseLeave={(e) => {
            if (!punchButtonsDisabled) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 10px rgba(16, 185, 129, 0.28)';
            }
          }}
        >
          {isLoading ? `⏳ ${t('attendance.processing')}` : `🏢 ${t('attendance.arrival')}`}
        </button>
        
        <button
          onClick={() => handleAttendance('out')}
          disabled={punchButtonsDisabled}
          style={{
            width: '100%',
            maxWidth: isGpsOnlyMode ? ((isMobile ? '220px' : '160px')) : '100%',
            padding: isGpsOnlyMode ? '9px 14px' : (isMobile ? '11px 14px' : '10px 16px'),
            fontSize: isGpsOnlyMode ? '12px' : (isMobile ? '13px' : '13px'),
            fontWeight: 700,
            border: 'none',
            borderRadius: isGpsOnlyMode ? '9px' : '10px',
            cursor: punchButtonsDisabled ? 'not-allowed' : 'pointer',
            opacity: punchButtonsDisabled ? 0.6 : 1,
            transition: 'all 0.3s ease',
            background: punchButtonsDisabled
              ? 'rgba(107, 114, 128, 0.5)'
              : 'linear-gradient(135deg, #ef4444, #dc2626)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isGpsOnlyMode ? '5px' : '6px',
            boxShadow: punchButtonsDisabled
              ? 'none'
              : '0 2px 10px rgba(239, 68, 68, 0.28)'
          }}
          onMouseEnter={(e) => {
            if (!punchButtonsDisabled) {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 14px rgba(239, 68, 68, 0.35)';
            }
          }}
          onMouseLeave={(e) => {
            if (!punchButtonsDisabled) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 10px rgba(239, 68, 68, 0.28)';
            }
          }}
        >
          {isLoading ? `⏳ ${t('attendance.processing')}` : `🚪 ${t('attendance.departure')}`}
        </button>
      </div>

      {!useGpsSidebar && manualOverridePanel}
      </div>
      </div>
      </>
      )}
      </div>

      {/* CSS pour animations */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (max-width: 767px) {
          .attendance-content {
            padding: 100px 16px 24px !important;
          }
          .attendance-work-location-box {
            padding: 12px !important;
            font-size: 0.85rem !important;
          }
        }
        @media (min-width: 768px) {
          .attendance-content {
            padding: 160px 40px 40px !important;
          }
        }
        @media (min-width: 1024px) {
          .attendance-content {
            padding: 180px 60px 60px !important;
          }
        }
        .attendance-content {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default GeolocationAttendance;